import React, { useEffect, useState, useMemo, useCallback, useRef, useImperativeHandle } from "react";
import {
  Alert,
  Button,
  Card,
  Group,
  Image,
  Loader,
  Stack,
  Text,
  Textarea,
  Title,
  TextInput,
  TagsInput,
  NumberInput,
  Accordion,
  Tabs,
  Badge,
  ActionIcon,
  Modal,
  Divider,
  Select,
  Box,
  Code,
  Switch,
  MultiSelect,
} from "@mantine/core";
import { useOne, useUpdate } from "@refinedev/core";
import { Link, useNavigate, useParams } from "react-router-dom";
import { IconTrash, IconEdit, IconX, IconPlus, IconWand, IconQuestionMark, IconRefresh, IconArrowUp, IconArrowDown, IconDownload, IconPhotoPlus, IconQrcode, IconFileUpload, IconGripVertical } from "@tabler/icons-react";
import { DragDropContext, Droppable, Draggable, DropResult } from "@hello-pangea/dnd";
import { Dropzone, FileWithPath } from "@mantine/dropzone";
import { notifications } from "@mantine/notifications";

import type { Menu, MenuPage, MenuItem } from "../../types";
import type { IngredientAnalysis, MenuItemFaqEntry } from "../../../types/menu";
import api from "../../../services/api";
import { MenuItemIngredientsV2 } from "../../components/MenuItemIngredientsV2";
import { getDisplayText, getCategoryList } from "../../../utils/translationHelpers";
import {
  uploadMenuImage,
  deleteMenuImage,
  uploadMenuItemImage,
  deleteMenuItemImage,
  uploadOnboardingModalImage,
  deleteOnboardingModalImage,
  uploadCategoryTopImage,
  deleteCategoryTopImage,
} from "../../../services/imageService";

// Recommended upload sizes aligned with the menu display design.
const IMAGE_DIMENSION_GUIDANCE = {
  menuHero: "1600 x 900 px (16:9)",
  categoryTop: "1600 x 900 px (16:9)",
  onboardingModal: "1200 x 900 px (4:3)",
  menuItem: "1600 x 900 px (16:9)",
};

const normalizeCategoryList = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return Array.from(
      new Set(
        value
          .map((entry) => (typeof entry === "string" ? entry.trim() : String(entry ?? "").trim()))
          .filter(Boolean)
      )
    );
  }

  if (typeof value === "string" && value.trim()) {
    return [value.trim()];
  }

  return [];
};

const getSourceCategoryList = (item: MenuItem): string[] => {
  const categories = normalizeCategoryList((item as any).categories);
  if (categories.length > 0) {
    return categories;
  }
  return normalizeCategoryList(item.category);
};

const getEnglishCategoryList = (item: MenuItem): string[] => {
  const props = (item as any)?.props || {};
  const enCategories = normalizeCategoryList(props?.translations?.en?.categoryList);
  if (enCategories.length > 0) {
    return enCategories;
  }

  const enCategoryText = props?.translations?.en?.category?.text || props?.enCategory?.text;
  if (enCategoryText) {
    return normalizeCategoryList(enCategoryText);
  }

  return [];
};

const itemHasCategory = (item: MenuItem, categoryName: string): boolean => {
  const sourceCategories = getSourceCategoryList(item);
  const englishCategories = getEnglishCategoryList(item);
  return sourceCategories.includes(categoryName) || englishCategories.includes(categoryName);
};

/**
 * Memoized Menu Item Editor Component
 * 
 * This component manages its own local state for all input fields to prevent
 * parent re-renders on every keystroke. It only updates the parent state when
 * saving or discarding changes.
 */
interface MenuItemEditorRef {
  save: () => Promise<void>;
  hasChanges: () => boolean;
  getLocalState: () => Partial<MenuItem>;
}

interface MenuItemEditorProps {
  item: MenuItem;
  onSave: (itemId: string, updates: Partial<MenuItem>) => Promise<void>;
  onDelete: (item: MenuItem) => void;
  onRedoAnalysis: (itemId: string) => void;
  onGenerateFaq: (itemId: string) => void;
  onGenerateImage: (itemId: string) => void;
  onUploadImage: (itemId: string, file: File) => Promise<void>;
  onDeleteImage: (itemId: string) => Promise<void>;
  isSaving: boolean;
  analysisLoading: boolean;
  faqLoading: boolean;
  imageLoading: boolean;
  onChangesChange?: (itemId: string, hasChanges: boolean) => void;
}

const MenuItemEditor = React.memo(
  React.forwardRef<MenuItemEditorRef, MenuItemEditorProps>(({
    item,
    onSave,
    onDelete,
    onRedoAnalysis,
    onGenerateFaq,
    onGenerateImage,
    onUploadImage,
    onDeleteImage,
    isSaving,
    analysisLoading,
    faqLoading,
    imageLoading,
    onChangesChange,
  }, ref) => {
  // Local state for all editable fields - only updates parent on save
  const [localState, setLocalState] = useState<Partial<MenuItem>>(() => {
    // Initialize with empty state - we'll use item values directly in inputs
    return {};
  });

  // Which ingredient index is currently generating an AI description (null = none)
  const [generatingDescriptionIdx, setGeneratingDescriptionIdx] = useState<number | null>(null);
  // Which ingredient index is currently generating missing name/desc fields (null = none)
  const [generatingMissingIdx, setGeneratingMissingIdx] = useState<number | null>(null);
  // Which FAQ entry index is currently generating an answer (null = none)
  const [generatingFaqAnswerIdx, setGeneratingFaqAnswerIdx] = useState<number | null>(null);

  // Track if there are any local changes
  const hasChanges = useMemo(() => {
    return Object.keys(localState).length > 0;
  }, [localState]);

  // Notify parent when changes status changes
  useEffect(() => {
    onChangesChange?.(item.id, hasChanges);
  }, [hasChanges, item.id, onChangesChange]);

  // Helper to get current value (local state or original item)
  const getValue = useCallback((field: keyof MenuItem, nestedField?: string): any => {
    if (nestedField) {
      // For nested translations (e.g., translations.en.name.text)
      const localValue = (localState as any)?.props?.translations?.en?.[nestedField]?.text;
      if (localValue !== undefined) return localValue;
      const itemValue = (item as any)?.props?.translations?.en?.[nestedField]?.text;
      if (itemValue !== undefined) return itemValue;
      // Fallback to getDisplayText
      const mergedItem = { ...item, ...localState };
      return getDisplayText(mergedItem as any, nestedField as any, "en");
    }
    return localState[field] ?? item[field];
  }, [localState, item]);

  const getSourceCategories = useCallback((): string[] => {
    if (Object.prototype.hasOwnProperty.call(localState, "categories")) {
      return normalizeCategoryList((localState as any)?.categories);
    }

    const itemCategories = normalizeCategoryList((item as any)?.categories);
    if (itemCategories.length > 0) {
      return itemCategories;
    }

    return normalizeCategoryList(localState.category ?? item.category);
  }, [item, localState]);

  const getEnCategories = useCallback((): string[] => {
    const localEnCategoryList = (localState as any)?.props?.translations?.en?.categoryList;
    if (localEnCategoryList !== undefined) {
      return normalizeCategoryList(localEnCategoryList);
    }

    const itemEnCategories = normalizeCategoryList((item as any)?.props?.translations?.en?.categoryList);
    if (itemEnCategories.length > 0) {
      return itemEnCategories;
    }

    const localEnText = (localState as any)?.props?.translations?.en?.category?.text;
    if (typeof localEnText === "string" && localEnText.trim()) {
      return normalizeCategoryList(localEnText);
    }

    const itemEnText = (item as any)?.props?.translations?.en?.category?.text || (item as any)?.props?.enCategory?.text;
    return normalizeCategoryList(itemEnText);
  }, [item, localState]);

  const handleSourceCategoriesChange = useCallback((values: string[]) => {
    const normalizedValues = normalizeCategoryList(values);
    setLocalState((prev) => ({
      ...prev,
      categories: normalizedValues,
      category: normalizedValues[0] || null,
    }));
  }, []);

  const handleEnCategoriesChange = useCallback((values: string[]) => {
    const normalizedValues = normalizeCategoryList(values);
    setLocalState((prev) => {
      const originalProps = item.props || {};
      const currentProps = prev.props ? { ...originalProps, ...prev.props } : originalProps;
      const currentTranslations = (currentProps as any)?.translations || {};
      const currentEn = currentTranslations.en || {};
      const currentEnCategory = currentEn.category || {};

      return {
        ...prev,
        props: {
          ...currentProps,
          translations: {
            ...currentTranslations,
            en: {
              ...currentEn,
              categoryList: normalizedValues,
              category: {
                ...currentEnCategory,
                text: normalizedValues[0] || "",
              },
            },
          },
        },
      };
    });
  }, [item.props]);

  // Handle basic field changes (name, category, price, description, imageUrl)
  const handleFieldChange = useCallback((field: keyof MenuItem, value: any) => {
    setLocalState((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  // Handle nested props changes (translations.en.name.text, etc.)
  const handleNestedChange = useCallback((path: string, value: any) => {
    setLocalState((prev) => {
      const pathSegments = path.split(".").filter(Boolean);
      // Merge original item's props with any existing local props
      // This ensures we preserve all existing translations when editing nested fields
      const originalProps = item.props || {};
      const currentProps = prev.props 
        ? { ...originalProps, ...prev.props }
        : originalProps;
      
      // Deep merge helper
      const setValueAtPath = (obj: any, segments: string[], val: any): any => {
        if (segments.length === 0) return val;
        const [first, ...rest] = segments;
        return {
          ...obj,
          [first]: setValueAtPath(obj?.[first] || {}, rest, val),
        };
      };

      const updatedProps = setValueAtPath(currentProps, pathSegments, value);

      return {
        ...prev,
        props: updatedProps,
      };
    });
  }, [item.props]);

  // Handle save - update parent state
  const handleSave = useCallback(async () => {
    await onSave(item.id, localState);
    setLocalState({}); // Clear local state after save
  }, [item.id, localState, onSave]);

  // Expose methods to parent via ref (must be after handleSave is defined)
  useImperativeHandle(ref, () => ({
    save: handleSave,
    hasChanges: () => hasChanges,
    getLocalState: () => localState,
  }), [handleSave, hasChanges, localState]);

  // Handle discard - clear local state
  const handleDiscard = useCallback(() => {
    setLocalState({});
  }, []);

  // Handle props JSON change
  const handlePropsJsonChange = useCallback((value: string) => {
    try {
      const parsed = JSON.parse(value);
      setLocalState((prev) => ({
        ...prev,
        props: parsed,
      }));
    } catch {
      // Invalid JSON, but allow typing
    }
  }, []);

  // Merged props (local overrides + original item) for reading current values
  const getCurrentProps = useCallback(() => {
    const originalProps = item.props || {};
    const localProps = localState.props;
    return localProps ? { ...originalProps, ...localProps } : originalProps;
  }, [item.props, localState.props]);

  // Update ingredients array (preserves other analysis fields)
  const handleSetIngredients = useCallback((ingredients: IngredientAnalysis[]) => {
    setLocalState((prev) => {
      const originalProps = item.props || {};
      const currentProps = prev.props ? { ...originalProps, ...prev.props } : originalProps;
      const currentAnalysis = (currentProps as any).analysis || {};
      return {
        ...prev,
        props: {
          ...currentProps,
          analysis: { ...currentAnalysis, ingredients },
        },
      };
    });
  }, [item.props]);

  // Update FAQ entries array (preserves menuFaq metadata, updates totalQuestions)
  const handleSetFaqEntries = useCallback((entries: MenuItemFaqEntry[]) => {
    setLocalState((prev) => {
      const originalProps = item.props || {};
      const currentProps = prev.props ? { ...originalProps, ...prev.props } : originalProps;
      const currentFaq = (currentProps as any).menuFaq || {
        entries: [],
        totalQuestions: 0,
        ai: false,
        generatedAt: new Date().toISOString(),
      };
      return {
        ...prev,
        props: {
          ...currentProps,
          menuFaq: {
            ...currentFaq,
            entries,
            totalQuestions: entries.length,
          },
        },
      };
    });
  }, [item.props]);

  /**
   * Call API to generate an English description for the ingredient at the given index,
   * using the same guidelines as ingredient analysis (gemini-2.5-flash-lite).
   */
  const handleGenerateIngredientDescription = useCallback(async (idx: number) => {
    const props = getCurrentProps();
    const ingredients: IngredientAnalysis[] = (props as any).analysis?.ingredients ?? [];
    const ing = ingredients[idx];
    if (!ing) return;
    setGeneratingDescriptionIdx(idx);
    try {
      const { data } = await api.post<{ description_en: string }>(
        "/menu-items/generate-ingredient-description",
        {
          canonical_name: ing.canonical_name || "",
          category: ing.category || "",
          rarity: ing.rarity || "common",
          name_en: ing.name_en ?? undefined,
          name_jp: ing.name_jp ?? undefined,
        }
      );
      const next = [...ingredients];
      next[idx] = { ...next[idx], description_en: data.description_en ?? "" };
      handleSetIngredients(next);
    } catch (err) {
      notifications.show({
        title: "Generate description failed",
        message: (err as Error)?.message ?? "Could not generate ingredient description",
        color: "red",
      });
    } finally {
      setGeneratingDescriptionIdx(null);
    }
  }, [getCurrentProps, handleSetIngredients]);

  /**
   * Call API to generate missing name_en, name_jp, description_en, description_jp for the ingredient at idx.
   * Only fills in fields that are currently empty (does not overwrite existing values).
   */
  const handleGenerateMissingIngredientFields = useCallback(async (idx: number) => {
    const props = getCurrentProps();
    const ingredients: IngredientAnalysis[] = (props as any).analysis?.ingredients ?? [];
    const ing = ingredients[idx];
    if (!ing) return;
    setGeneratingMissingIdx(idx);
    try {
      const { data } = await api.post<{
        name_en?: string;
        name_jp?: string;
        description_en?: string;
        description_jp?: string;
      }>("/menu-items/generate-ingredient-fields", {
        canonical_name: ing.canonical_name || "",
        category: ing.category || "",
        rarity: ing.rarity || "common",
        name_en: ing.name_en ?? undefined,
        name_jp: ing.name_jp ?? undefined,
        description_en: ing.description_en ?? undefined,
        description_jp: ing.description_jp ?? undefined,
      });
      const next = [...ingredients];
      next[idx] = {
        ...next[idx],
        name_en: (ing.name_en && ing.name_en.trim()) ? ing.name_en : (data.name_en ?? ing.name_en ?? ""),
        name_jp: (ing.name_jp && ing.name_jp.trim()) ? ing.name_jp : (data.name_jp ?? ing.name_jp ?? ""),
        description_en: (ing.description_en && ing.description_en.trim()) ? ing.description_en : (data.description_en ?? ing.description_en ?? ""),
        description_jp: (ing.description_jp && ing.description_jp.trim()) ? ing.description_jp : (data.description_jp ?? ing.description_jp ?? ""),
      };
      handleSetIngredients(next);
    } catch (err) {
      notifications.show({
        title: "Generate missing fields failed",
        message: (err as Error)?.message ?? "Could not generate ingredient name/description",
        color: "red",
      });
    } finally {
      setGeneratingMissingIdx(null);
    }
  }, [getCurrentProps, handleSetIngredients]);

  /**
   * Generate an AI answer for a user-authored FAQ question.
   * This updates only local editor state so admins can review before saving.
   */
  const handleGenerateFaqAnswer = useCallback(async (idx: number, question: string) => {
    const trimmedQuestion = question.trim();
    if (!trimmedQuestion) {
      notifications.show({
        title: "Question required",
        message: "Write a question first, then generate the answer.",
        color: "yellow",
      });
      return;
    }

    setGeneratingFaqAnswerIdx(idx);
    try {
      const { data } = await api.post<{ answer: string }>(`/menu-items/${item.id}/faq-answer`, {
        question: trimmedQuestion,
      });

      const props = getCurrentProps();
      const faqPayload = (props as any).menuFaq;
      const entries: MenuItemFaqEntry[] = faqPayload?.entries ?? [];
      const next = [...entries];
      if (!next[idx]) {
        next[idx] = { index: idx + 1, question: trimmedQuestion, answer: "" };
      }
      next[idx] = { ...next[idx], question: trimmedQuestion, answer: data.answer ?? "" };
      handleSetFaqEntries(next);
    } catch (err: any) {
      notifications.show({
        title: "Generate answer failed",
        message: err?.response?.data?.error || err?.message || "Could not generate FAQ answer",
        color: "red",
      });
    } finally {
      setGeneratingFaqAnswerIdx(null);
    }
  }, [getCurrentProps, handleSetFaqEntries, item.id]);

  // Calculate display values for accordion header
  const displayName = useMemo(() => localState.name ?? item.name, [localState.name, item.name]);
  const displayPrice = useMemo(() => localState.price ?? item.price, [localState.price, item.price]);
  const enName = useMemo(() => {
    const mergedItem = { ...item, ...localState };
    return getDisplayText(mergedItem as any, "name", "en");
  }, [item, localState]);

  return (
    <Accordion.Item value={item.id}>
      <Accordion.Control>
        <Group justify="space-between" wrap="nowrap">
          <Group gap="xs" wrap="nowrap">
            <Text fw={500} truncate>
              {displayName}
            </Text>
            {enName && enName !== displayName && (
              <Text size="sm" c="dimmed" truncate>
                / {enName}
              </Text>
            )}
            <Text size="sm" c="dimmed">
              ¥{displayPrice ?? "—"}
            </Text>
            {hasChanges && (
              <Badge size="xs" color="yellow" variant="light">
                Unsaved
              </Badge>
            )}
          </Group>
          <ActionIcon
            component="div"
            color="red"
            variant="subtle"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(item);
            }}
            style={{ cursor: 'pointer' }}
          >
            <IconTrash size={16} />
          </ActionIcon>
        </Group>
      </Accordion.Control>
      <Accordion.Panel>
        <Tabs defaultValue="basic">
          <Tabs.List>
            <Tabs.Tab value="basic">Basic Info</Tabs.Tab>
            <Tabs.Tab value="translations">Translations</Tabs.Tab>
            <Tabs.Tab value="advanced">Advanced</Tabs.Tab>
            <Tabs.Tab value="image">Image</Tabs.Tab>
          </Tabs.List>

          {/* Basic Info Tab */}
          <Tabs.Panel value="basic" pt="md">
            <Stack gap="md">
              <Group grow>
                <TextInput
                  label="Name (Original)"
                  value={getValue("name") || ""}
                  onChange={(e) => handleFieldChange("name", e.currentTarget.value)}
                />
                <TagsInput
                  label="Categories (Original)"
                  description="One menu item can belong to multiple categories."
                  data={[]}
                  value={getSourceCategories()}
                  onChange={handleSourceCategoriesChange}
                  placeholder="Add category and press Enter"
                  clearable
                />
                <NumberInput
                  label="Price"
                  value={getValue("price") ? Number(getValue("price")) : undefined}
                  onChange={(value) => handleFieldChange("price", value ?? null)}
                  decimalScale={2}
                  min={0}
                  prefix="¥"
                />
              </Group>
              <Textarea
                label="Description (Original)"
                minRows={4}
                value={getValue("description") || ""}
                onChange={(e) => handleFieldChange("description", e.currentTarget.value)}
              />
            </Stack>
          </Tabs.Panel>

          {/* Translations Tab */}
          <Tabs.Panel value="translations" pt="md">
            <Stack gap="md">
              <Card withBorder p="md">
                <Title order={5} mb="md">
                  English Localized Translation
                </Title>
                <Stack gap="md">
                  <Group grow>
                    <TextInput
                      label="EN Name (Localized)"
                      value={getValue("name", "name") || ""}
                      onChange={(e) => handleNestedChange("translations.en.name.text", e.currentTarget.value)}
                    />
                    <TagsInput
                      label="EN Categories"
                      description="English category list used for EN menu grouping."
                      data={[]}
                      value={getEnCategories()}
                      onChange={handleEnCategoriesChange}
                      placeholder="Add English category and press Enter"
                      clearable
                    />
                  </Group>
                  <Textarea
                    label="EN Description (Localized)"
                    minRows={4}
                    value={getValue("description", "description") || ""}
                    onChange={(e) => handleNestedChange("translations.en.description.text", e.currentTarget.value)}
                  />
                  <Textarea
                    label="EN Disclaimer"
                    description="Optional disclaimer shown beneath the item description (e.g. allergen notice, serving info)."
                    minRows={2}
                    value={(() => {
                      const props = getCurrentProps();
                      return (props as any)?.translations?.en?.disclaimer?.text ?? "";
                    })()}
                    onChange={(e) => handleNestedChange("translations.en.disclaimer.text", e.currentTarget.value)}
                    placeholder="e.g. Contains shellfish. May contain traces of nuts."
                  />
                  <Textarea
                    label="EN Background"
                    description="Background story or extra context shown on the item detail (e.g. origin, history, chef's note)."
                    minRows={3}
                    value={(() => {
                      const props = getCurrentProps();
                      return (props as any)?.translations?.en?.background?.text ?? "";
                    })()}
                    onChange={(e) => handleNestedChange("translations.en.background.text", e.currentTarget.value)}
                    placeholder="e.g. This wagyu comes from a small family farm in Kagoshima Prefecture…"
                  />
                </Stack>
              </Card>
            </Stack>
          </Tabs.Panel>

          {/* Advanced Tab */}
          <Tabs.Panel value="advanced" pt="md">
            <Stack gap="md">
              <Card withBorder p="md" style={{ backgroundColor: "#f8f9fa" }}>
                <Title order={5} mb="md">AI Actions</Title>
                <Group gap="sm">
                  <Button
                    size="xs"
                    leftSection={<IconRefresh size={14} />}
                    onClick={() => onRedoAnalysis(item.id)}
                    loading={analysisLoading}
                    variant="light"
                    color="green"
                  >
                    Redo Analysis
                  </Button>
                  <Button
                    size="xs"
                    leftSection={<IconQuestionMark size={14} />}
                    onClick={() => onGenerateFaq(item.id)}
                    loading={faqLoading}
                    variant="light"
                    color="green"
                  >
                    Generate FAQ
                  </Button>
                  <Button
                    size="xs"
                    leftSection={<IconPhotoPlus size={14} />}
                    onClick={() => onGenerateImage(item.id)}
                    loading={imageLoading}
                    variant="light"
                    color="blue"
                  >
                    Generate AI Image
                  </Button>
                </Group>
              </Card>

              <Card withBorder p="md">
                <Title order={5} mb="md">Ingredients Analysis</Title>
                <Text size="sm" c="dimmed" mb="md">
                  View, edit, add, or remove ingredients. Changes are saved when you click &quot;Save Item&quot; below.
                </Text>
                {(() => {
                  const props = getCurrentProps();
                  const ingredients: IngredientAnalysis[] = (props as any).analysis?.ingredients ?? [];
                  const rarityOptions = [
                    { value: "common", label: "Common" },
                    { value: "uncommon", label: "Uncommon" },
                    { value: "rare", label: "Rare" },
                    { value: "very_rare", label: "Very Rare" },
                  ];
                  return (
                    <Stack gap="md">
                      {ingredients.length === 0 && (
                        <Text size="sm" c="dimmed">No ingredients yet. Add one below.</Text>
                      )}
                      <Accordion
                        multiple
                        variant="separated"
                        defaultValue={[]}
                        styles={{ content: { padding: "var(--mantine-spacing-xs)" } }}
                      >
                        {ingredients.map((ing, idx) => {
                          // Summary line for collapsed state: prefer name_en or canonical_name
                          const summary = [ing.name_en, ing.canonical_name, ing.name_jp].filter(Boolean)[0] ?? "—";
                          return (
                            <Accordion.Item key={idx} value={`ingredient-${idx}`}>
                              <Accordion.Control>
                                <Group justify="space-between" wrap="nowrap">
                                  <Text size="sm" fw={500}>
                                    Ingredient {idx + 1}
                                    {summary !== "—" && (
                                      <Text component="span" size="xs" c="dimmed" ml="xs" fw={400}>
                                        — {summary}
                                      </Text>
                                    )}
                                  </Text>
                                  <ActionIcon
                                    component="div"
                                    color="red"
                                    variant="subtle"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const next = ingredients.filter((_, i) => i !== idx);
                                      handleSetIngredients(next);
                                    }}
                                    title="Remove ingredient"
                                    style={{ cursor: "pointer" }}
                                  >
                                    <IconTrash size={14} />
                                  </ActionIcon>
                                </Group>
                              </Accordion.Control>
                              <Accordion.Panel>
                                <Stack gap="xs">
                                  <TextInput
                                    label="Canonical name"
                                    size="xs"
                                    value={ing.canonical_name ?? ""}
                                    onChange={(e) => {
                                      const next = [...ingredients];
                                      next[idx] = { ...next[idx], canonical_name: e.currentTarget.value };
                                      handleSetIngredients(next);
                                    }}
                                  />
                                  <Group grow>
                                    <TextInput
                                      label="Name (EN)"
                                      size="xs"
                                      value={ing.name_en ?? ""}
                                      onChange={(e) => {
                                        const next = [...ingredients];
                                        next[idx] = { ...next[idx], name_en: e.currentTarget.value };
                                        handleSetIngredients(next);
                                      }}
                                      placeholder="English name"
                                    />
                                    <TextInput
                                      label="Name (JP)"
                                      size="xs"
                                      value={ing.name_jp ?? ""}
                                      onChange={(e) => {
                                        const next = [...ingredients];
                                        next[idx] = { ...next[idx], name_jp: e.currentTarget.value };
                                        handleSetIngredients(next);
                                      }}
                                      placeholder="Japanese name"
                                    />
                                  </Group>
                                  <Group grow>
                                    <TextInput
                                      label="Category"
                                      size="xs"
                                      value={ing.category ?? ""}
                                      onChange={(e) => {
                                        const next = [...ingredients];
                                        next[idx] = { ...next[idx], category: e.currentTarget.value };
                                        handleSetIngredients(next);
                                      }}
                                    />
                                    <Select
                                      label="Rarity"
                                      size="xs"
                                      data={rarityOptions}
                                      value={ing.rarity ?? "common"}
                                      onChange={(value) => {
                                        if (!value) return;
                                        const next = [...ingredients];
                                        next[idx] = { ...next[idx], rarity: value as IngredientAnalysis["rarity"] };
                                        handleSetIngredients(next);
                                      }}
                                    />
                                  </Group>
                                  <Group align="flex-end" wrap="nowrap">
                                    <TextInput
                                      label="Description (EN)"
                                      size="xs"
                                      style={{ flex: 1 }}
                                      value={ing.description_en ?? ""}
                                      onChange={(e) => {
                                        const next = [...ingredients];
                                        next[idx] = { ...next[idx], description_en: e.currentTarget.value };
                                        handleSetIngredients(next);
                                      }}
                                    />
                                    <Button
                                      size="xs"
                                      variant="light"
                                      leftSection={<IconWand size={14} />}
                                      loading={generatingDescriptionIdx === idx}
                                      onClick={() => handleGenerateIngredientDescription(idx)}
                                      title="Generate an AI description (same guidelines as ingredient analysis)"
                                    >
                                      Generate
                                    </Button>
                                  </Group>
                                  <Textarea
                                    label="Description (JP)"
                                    size="xs"
                                    minRows={2}
                                    value={ing.description_jp ?? ""}
                                    onChange={(e) => {
                                      const next = [...ingredients];
                                      next[idx] = { ...next[idx], description_jp: e.currentTarget.value };
                                      handleSetIngredients(next);
                                    }}
                                    placeholder="Japanese description"
                                  />
                                  <Button
                                    size="xs"
                                    variant="light"
                                    leftSection={<IconWand size={14} />}
                                    loading={generatingMissingIdx === idx}
                                    onClick={() => handleGenerateMissingIngredientFields(idx)}
                                    title="Generate missing name/description (EN & JP) using AI"
                                  >
                                    Generate missing name / desc
                                  </Button>
                                  <Group>
                                    <Text size="xs" c="dimmed">Display:</Text>
                                    <Button
                                      size="xs"
                                      variant={ing.display ? "filled" : "light"}
                                      onClick={() => {
                                        const next = [...ingredients];
                                        next[idx] = { ...next[idx], display: !ing.display };
                                        handleSetIngredients(next);
                                      }}
                                    >
                                      {ing.display ? "Yes" : "No"}
                                    </Button>
                                  </Group>
                                </Stack>
                              </Accordion.Panel>
                            </Accordion.Item>
                          );
                        })}
                      </Accordion>
                      <Button
                        leftSection={<IconPlus size={14} />}
                        variant="light"
                        size="xs"
                        onClick={() => {
                          const next = [
                            ...ingredients,
                            {
                              canonical_name: "",
                              category: "",
                              rarity: "common" as const,
                              display: true,
                            },
                          ];
                          handleSetIngredients(next);
                        }}
                      >
                        Add ingredient
                      </Button>
                    </Stack>
                  );
                })()}
              </Card>

              <Card withBorder p="md">
                <MenuItemIngredientsV2 menuItemId={item.id} />
              </Card>

              <Card withBorder p="md">
                <Title order={5} mb="md">FAQ</Title>
                <Text size="sm" c="dimmed" mb="md">
                  View, edit, add, or remove FAQ entries. Changes are saved when you click &quot;Save Item&quot; below.
                </Text>
                {(() => {
                  const props = getCurrentProps();
                  const faqPayload = (props as any).menuFaq;
                  const entries: MenuItemFaqEntry[] = faqPayload?.entries ?? [];
                  return (
                    <Stack gap="md">
                      {entries.length === 0 && (
                        <Text size="sm" c="dimmed">No FAQ entries yet. Add one below.</Text>
                      )}
                      {entries.map((entry, idx) => (
                        <Box key={idx} p="sm" style={{ border: "1px solid var(--mantine-color-default-border)", borderRadius: "var(--mantine-radius-sm)" }}>
                          <Group justify="space-between" mb="xs">
                            <Text size="sm" fw={500}>FAQ {idx + 1}</Text>
                            <ActionIcon
                              color="red"
                              variant="subtle"
                              size="sm"
                              onClick={() => {
                                const next = entries.filter((_, i) => i !== idx).map((e, i) => ({ ...e, index: i }));
                                handleSetFaqEntries(next);
                              }}
                            >
                              <IconTrash size={14} />
                            </ActionIcon>
                          </Group>
                          <Stack gap="xs">
                            <TextInput
                              label="Question"
                              size="xs"
                              value={entry.question ?? ""}
                              onChange={(e) => {
                                const next = [...entries];
                                next[idx] = { ...next[idx], question: e.currentTarget.value };
                                handleSetFaqEntries(next);
                              }}
                            />
                            <Group justify="flex-end">
                              <Button
                                size="xs"
                                variant="light"
                                leftSection={<IconWand size={14} />}
                                loading={generatingFaqAnswerIdx === idx}
                                disabled={!(entry.question ?? "").trim()}
                                onClick={() => handleGenerateFaqAnswer(idx, entry.question ?? "")}
                              >
                                Generate answer
                              </Button>
                            </Group>
                            <Textarea
                              label="Answer"
                              size="xs"
                              minRows={2}
                              value={entry.answer ?? ""}
                              onChange={(e) => {
                                const next = [...entries];
                                next[idx] = { ...next[idx], answer: e.currentTarget.value };
                                handleSetFaqEntries(next);
                              }}
                            />
                          </Stack>
                        </Box>
                      ))}
                      <Button
                        leftSection={<IconPlus size={14} />}
                        variant="light"
                        size="xs"
                        onClick={() => {
                          const next = [
                            ...entries,
                            { index: entries.length, question: "", answer: "" },
                          ];
                          handleSetFaqEntries(next);
                        }}
                      >
                        Add FAQ entry
                      </Button>
                    </Stack>
                  );
                })()}
              </Card>

              <Card withBorder p="md">
                <Title order={5} mb="md">Raw Props (JSON)</Title>
                <Textarea
                  label="Props JSON"
                  description="Edit the entire props object as JSON. Be careful - invalid JSON will cause errors."
                  minRows={10}
                  value={JSON.stringify(
                    localState.props ?? (item as any)?.props ?? {},
                    null,
                    2,
                  )}
                  onChange={(e) => handlePropsJsonChange(e.currentTarget.value)}
                  style={{ fontFamily: "monospace" }}
                />
              </Card>
            </Stack>
          </Tabs.Panel>

          {/* Image Tab */}
          <Tabs.Panel value="image" pt="md">
            <Stack gap="md">
              {item.imageUrl ? (
                <Card withBorder p="md">
                  <Text fw={500} mb="sm">
                    Current Image
                  </Text>
                  <Image
                    src={item.imageUrl}
                    alt={item.name}
                    maw={300}
                    fit="contain"
                  />
                </Card>
              ) : (
                <Text c="dimmed">No image uploaded</Text>
              )}
              <Group>
                <label>
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        onUploadImage(item.id, file);
                        // Clear the input so selecting the same file again still triggers onChange.
                        e.currentTarget.value = "";
                      }
                    }}
                    disabled={imageLoading}
                  />
                  <Button
                    component="span"
                    variant="light"
                    leftSection={<IconPhotoPlus size={14} />}
                    loading={imageLoading}
                    disabled={imageLoading}
                  >
                    {item.imageUrl ? "Replace Image" : "Upload Image"}
                  </Button>
                </label>
                {item.imageUrl ? (
                  <Button
                    color="red"
                    variant="light"
                    leftSection={<IconTrash size={14} />}
                    onClick={() => onDeleteImage(item.id)}
                    loading={imageLoading}
                    disabled={imageLoading}
                  >
                    Remove Image
                  </Button>
                ) : null}
              </Group>
              <Text size="xs" c="dimmed">
                Recommended dimensions: <Code>{IMAGE_DIMENSION_GUIDANCE.menuItem}</Code>
              </Text>
              <Text size="xs" c="dimmed">
                Use a landscape image (width longer than height) and keep the main food subject centered so mobile crops still look good.
              </Text>
              <TextInput
                label="Image URL"
                value={getValue("imageUrl") || ""}
                onChange={(e) => handleFieldChange("imageUrl", e.currentTarget.value)}
                placeholder="Enter Cloudinary URL or image URL"
              />
            </Stack>
          </Tabs.Panel>
        </Tabs>

        {/* Save/Discard buttons outside tabs */}
        <Group justify="flex-end" mt="md">
          {hasChanges && (
            <Button variant="subtle" onClick={handleDiscard}>
              Discard
            </Button>
          )}
          <Button
            loading={isSaving}
            disabled={!hasChanges}
            onClick={handleSave}
          >
            Save Item
          </Button>
        </Group>
      </Accordion.Panel>
    </Accordion.Item>
  );
}), (prevProps, nextProps) => {
  // Custom comparison: only re-render if item data changes or saving state changes
  // This prevents re-renders when other items are edited
  return (
    prevProps.item.id === nextProps.item.id &&
    prevProps.item.name === nextProps.item.name &&
    prevProps.item.category === nextProps.item.category &&
    prevProps.item.price === nextProps.item.price &&
    prevProps.item.description === nextProps.item.description &&
    prevProps.item.imageUrl === nextProps.item.imageUrl &&
    JSON.stringify(prevProps.item.props) === JSON.stringify(nextProps.item.props) &&
    prevProps.isSaving === nextProps.isSaving &&
    prevProps.analysisLoading === nextProps.analysisLoading &&
    prevProps.faqLoading === nextProps.faqLoading &&
    prevProps.imageLoading === nextProps.imageLoading
  );
});

MenuItemEditor.displayName = "MenuItemEditor";

/**
 * Admin Menu Edit page with support for editing menu pages and menu items.
 * 
 * This component allows admins to:
 * - Edit menu metadata (admin notes, category order, query setup)
 * - Edit menu pages (admin notes, image URL)
 * - Edit menu items (name, description, price, category)
 */
interface DedupeItem {
  id: string;
  name: string;
  description: string | null;
  price: number | null;
  category: string | null;
  imageUrl: string | null;
  score: number;
}
interface DedupeGroup {
  name: string;
  items: DedupeItem[];
}

export function MenuEdit() {
  const navigate = useNavigate();
  const { id } = useParams();

  // Fetch menu data with pages and items
  const { data, isLoading, refetch } = useOne<Menu>({
    resource: "menus",
    id: id as string,
    queryOptions: {
      // Prevent auto-refetch on window focus resetting unsaved form edits
      refetchOnWindowFocus: false,
    },
  });

  const { mutateAsync: updateMenuAsync, isLoading: isSavingMenu } = useUpdate<Menu>();

  const menu = data?.data;

  // Menu-level state
  const [adminNotes, setAdminNotes] = useState<string>("");
  const [categoryOrderText, setCategoryOrderText] = useState<string>("");
  const [querySetupText, setQuerySetupText] = useState<string>("");
  const [onboardingContent, setOnboardingContent] = useState<{
    languageConfigs?: {
      [languageCode: string]: {
        title?: string;
        sections?: Array<{
          title?: string;
          content: string;
        }>;
        buttonText?: string;
        questionChips?: string[];
      };
    };
    defaultLanguage?: string;
    fallbackConfig?: {
      title?: string;
      sections?: Array<{
        title?: string;
        content: string;
      }>;
      buttonText?: string;
      questionChips?: string[];
    };
  }>({});
  const [drinkSectionEnabled, setDrinkSectionEnabled] = useState(false);
  const [drinkCategories, setDrinkCategories] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Menu page editing state
  const [editedPages, setEditedPages] = useState<Record<string, Partial<MenuPage>>>({});
  const [savingPageId, setSavingPageId] = useState<string | null>(null);

  // Menu item editing state
  const [editedItems, setEditedItems] = useState<Record<string, Partial<MenuItem>>>({});
  const [savingItemId, setSavingItemId] = useState<string | null>(null);
  // Track which items have unsaved changes (for Save All functionality)
  const [itemsWithChanges, setItemsWithChanges] = useState<Set<string>>(new Set());
  // Refs to access MenuItemEditor components for Save All
  const itemEditorRefs = useRef<Record<string, React.RefObject<MenuItemEditorRef>>>({});
  const [itemToDelete, setItemToDelete] = useState<MenuItem | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [creatingItem, setCreatingItem] = useState<string | null>(null); // pageId where item is being created
  const [newItemData, setNewItemData] = useState<Partial<MenuItem>>({
    name: "",
    description: "",
    price: null,
    category: "",
  });

  // CSV import into current page: which page's modal is open, selected file, loading
  const [csvImportPageId, setCsvImportPageId] = useState<string | null>(null);
  const [csvImportFile, setCsvImportFile] = useState<FileWithPath | null>(null);
  const [csvImportLoading, setCsvImportLoading] = useState(false);

  // Menu page creation/deletion state
  const [pageToDelete, setPageToDelete] = useState<MenuPage | null>(null);
  const [deletePageModalOpen, setDeletePageModalOpen] = useState(false);
  const [creatingPage, setCreatingPage] = useState(false);
  const [newPageData, setNewPageData] = useState<Partial<MenuPage>>({
    imageUrl: "",
    adminNotes: "",
  });

  // AI actions state
  const [redoingAnalysis, setRedoingAnalysis] = useState(false);
  const [generatingFaqs, setGeneratingFaqs] = useState(false);
  const [generatingTestFaq, setGeneratingTestFaq] = useState(false);
  const [generatingOnboardingContent, setGeneratingOnboardingContent] = useState(false);
  const [itemAnalysisLoading, setItemAnalysisLoading] = useState<Record<string, boolean>>({});
  const [itemFaqLoading, setItemFaqLoading] = useState<Record<string, boolean>>({});
  const [itemImageLoading, setItemImageLoading] = useState<Record<string, boolean>>({});

  // Dedupe modal state
  const [dedupeScanning, setDedupeScanning] = useState(false);
  const [dedupeModalOpen, setDedupeModalOpen] = useState(false);
  const [dedupeGroups, setDedupeGroups] = useState<DedupeGroup[]>([]);
  // Set of item IDs checked for deletion (pre-populated with all but the best-scored per group)
  const [dedupeSelected, setDedupeSelected] = useState<Set<string>>(new Set());
  const [dedupeDeleting, setDedupeDeleting] = useState(false);

  // FAQ test preview modal state (does NOT persist to menu)
  const [testFaqModalOpen, setTestFaqModalOpen] = useState(false);
  const [testFaqSelectedItemId, setTestFaqSelectedItemId] = useState<string | null>(null);
  const [testFaqResult, setTestFaqResult] = useState<any | null>(null);

  // Category management state
  const [categoryRenameModalOpen, setCategoryRenameModalOpen] = useState(false);
  const [categoryToRename, setCategoryToRename] = useState<string | null>(null);
  const [newCategoryName, setNewCategoryName] = useState<string>("");
  const [renamingCategory, setRenamingCategory] = useState(false);

  // Image upload state
  const [uploadingMenuHeaderImage, setUploadingMenuHeaderImage] = useState(false);
  const [deletingMenuHeaderImage, setDeletingMenuHeaderImage] = useState(false);
  const [uploadingOnboardingModalImage, setUploadingOnboardingModalImage] = useState(false);
  const [deletingOnboardingModalImage, setDeletingOnboardingModalImage] = useState(false);
  const [uploadingCategoryTopImage, setUploadingCategoryTopImage] = useState(false);
  const [deletingCategoryTopImage, setDeletingCategoryTopImage] = useState(false);

  // QR code generation state
  const [generatingQrCode, setGeneratingQrCode] = useState(false);

  // Initialize form state when menu data loads
  useEffect(() => {
    if (!menu) return;

    setAdminNotes(menu.adminNotes ?? "");
    setCategoryOrderText(menu.categoryOrder ? JSON.stringify(menu.categoryOrder, null, 2) : "");
    setQuerySetupText(menu.querySetup ? JSON.stringify(menu.querySetup, null, 2) : "");
    if (menu.onboardingContent) {
      setOnboardingContent(menu.onboardingContent);
    }
    setDrinkSectionEnabled(menu.drinkSection?.enabled ?? false);
    setDrinkCategories(menu.drinkSection?.drinkCategories ?? []);
  }, [menu]);

  /**
   * Helper function to safely update onboarding content sections
   */
  const updateOnboardingSection = (
    languageCode: string | 'fallback',
    sectionIndex: number,
    field: 'title' | 'content',
    value: string
  ) => {
    setOnboardingContent(prev => {
      const newContent = { ...prev };
      
      if (languageCode === 'fallback') {
        if (!newContent.fallbackConfig) {
          newContent.fallbackConfig = { sections: [] };
        }
        if (!newContent.fallbackConfig.sections) {
          newContent.fallbackConfig.sections = [];
        }
        if (!newContent.fallbackConfig.sections[sectionIndex]) {
          newContent.fallbackConfig.sections[sectionIndex] = { content: '' };
        }
        newContent.fallbackConfig.sections[sectionIndex] = {
          ...newContent.fallbackConfig.sections[sectionIndex],
          [field]: field === 'content' ? value : (value || undefined)
        };
      } else {
        if (!newContent.languageConfigs) {
          newContent.languageConfigs = {};
        }
        if (!newContent.languageConfigs[languageCode]) {
          newContent.languageConfigs[languageCode] = { sections: [] };
        }
        if (!newContent.languageConfigs[languageCode].sections) {
          newContent.languageConfigs[languageCode].sections = [];
        }
        if (!newContent.languageConfigs[languageCode].sections![sectionIndex]) {
          newContent.languageConfigs[languageCode].sections![sectionIndex] = { content: '' };
        }
        newContent.languageConfigs[languageCode].sections![sectionIndex] = {
          ...newContent.languageConfigs[languageCode].sections![sectionIndex],
          [field]: field === 'content' ? value : (value || undefined)
        };
      }
      
      return newContent;
    });
  };

  /**
   * Handle generating onboarding content with AI
   */
  const handleGenerateOnboardingContent = async () => {
    if (!menu || !id) return;

    setGeneratingOnboardingContent(true);
    try {
      const response = await api.post(`/menus/${id}/onboarding-content`);
      const generatedContent = response.data.onboardingContent;

      // Populate the fallback config with generated content
      setOnboardingContent({
        ...onboardingContent,
        fallbackConfig: generatedContent.fallbackConfig
      });

      notifications.show({
        title: "Success",
        message: "Onboarding content generated successfully",
        color: "green",
      });
    } catch (error: any) {
      console.error('Error generating onboarding content:', error);
      notifications.show({
        title: "Error",
        message: error?.response?.data?.error || error?.message || "Failed to generate onboarding content",
        color: "red",
      });
    } finally {
      setGeneratingOnboardingContent(false);
    }
  };

  /**
   * Handle saving menu-level metadata
   */
  const handleSaveMenu = async () => {
    if (!menu) return;

    try {
      setError(null);

      const parseOptionalJson = (text: string, label: string) => {
        if (!text.trim()) return null;
        try {
          return JSON.parse(text);
        } catch {
          throw new Error(`${label} must be valid JSON.`);
        }
      };

      const categoryOrder = parseOptionalJson(categoryOrderText, "Category order");
      const querySetup = parseOptionalJson(querySetupText, "Query setup");

      // Check if onboardingContent has any actual content (not just empty object)
      const hasOnboardingContent = onboardingContent && (
        (onboardingContent.languageConfigs && Object.keys(onboardingContent.languageConfigs).length > 0) ||
        (onboardingContent.fallbackConfig && (
          onboardingContent.fallbackConfig.title ||
          onboardingContent.fallbackConfig.buttonText ||
          (onboardingContent.fallbackConfig.sections && onboardingContent.fallbackConfig.sections.length > 0) ||
          (onboardingContent.fallbackConfig.questionChips && onboardingContent.fallbackConfig.questionChips.length > 0)
        ))
      );

      await updateMenuAsync({
        resource: "menus",
        id: menu.id,
        values: {
          adminNotes: adminNotes.trim() || null,
          categoryOrder,
          querySetup,
          onboardingContent: hasOnboardingContent ? onboardingContent : null,
          drinkSection: drinkSectionEnabled
            ? { enabled: true, drinkCategories }
            : null,
        },
      });

      notifications.show({
        title: "Success",
        message: "Menu updated successfully",
        color: "green",
      });
    } catch (e: any) {
      setError(e?.message ?? "Failed to save menu");
      notifications.show({
        title: "Error",
        message: e?.message ?? "Failed to save menu",
        color: "red",
      });
    }
  };

  /**
   * Handle uploading menu header image
   */
  const handleUploadMenuHeaderImage = async (file: File) => {
    if (!menu || !id) return;

    setUploadingMenuHeaderImage(true);
    try {
      await uploadMenuImage(id, file);
      notifications.show({
        title: "Success",
        message: "Menu header image uploaded successfully",
        color: "green",
      });
      await refetch();
    } catch (error: any) {
      notifications.show({
        title: "Error",
        message: error?.response?.data?.error || error?.message || "Failed to upload menu header image",
        color: "red",
      });
    } finally {
      setUploadingMenuHeaderImage(false);
    }
  };

  /**
   * Handle deleting menu header image
   */
  const handleDeleteMenuHeaderImage = async () => {
    if (!menu || !id) return;

    const confirmed = window.confirm("Are you sure you want to delete the menu header image?");
    if (!confirmed) return;

    setDeletingMenuHeaderImage(true);
    try {
      await deleteMenuImage(id);
      notifications.show({
        title: "Success",
        message: "Menu header image deleted successfully",
        color: "green",
      });
      await refetch();
    } catch (error: any) {
      notifications.show({
        title: "Error",
        message: error?.response?.data?.error || error?.message || "Failed to delete menu header image",
        color: "red",
      });
    } finally {
      setDeletingMenuHeaderImage(false);
    }
  };

  /**
   * Generate or regenerate QR code for this menu (calls backend, then refetches menu)
   */
  const handleGenerateQrCode = async () => {
    if (!menu?.id) return;

    setGeneratingQrCode(true);
    try {
      await api.post(`/menus/${menu.id}/generateqrcode`);
      notifications.show({
        title: "Success",
        message: "QR code generated successfully",
        color: "green",
      });
      await refetch();
    } catch (error: any) {
      notifications.show({
        title: "Error",
        message: error?.response?.data?.error || error?.message || "Failed to generate QR code",
        color: "red",
      });
    } finally {
      setGeneratingQrCode(false);
    }
  };

  /**
   * Handle uploading onboarding modal image
   */
  const handleUploadOnboardingModalImage = async (file: File) => {
    if (!menu || !id) return;

    setUploadingOnboardingModalImage(true);
    try {
      await uploadOnboardingModalImage(id, file);
      notifications.show({
        title: "Success",
        message: "Onboarding modal image uploaded successfully",
        color: "green",
      });
      await refetch();
    } catch (error: any) {
      notifications.show({
        title: "Error",
        message: error?.response?.data?.error || error?.message || "Failed to upload onboarding modal image",
        color: "red",
      });
    } finally {
      setUploadingOnboardingModalImage(false);
    }
  };

  /**
   * Handle deleting onboarding modal image
   */
  const handleDeleteOnboardingModalImage = async () => {
    if (!menu || !id) return;

    const confirmed = window.confirm("Are you sure you want to delete the onboarding modal image?");
    if (!confirmed) return;

    setDeletingOnboardingModalImage(true);
    try {
      await deleteOnboardingModalImage(id);
      notifications.show({
        title: "Success",
        message: "Onboarding modal image deleted successfully",
        color: "green",
      });
      await refetch();
    } catch (error: any) {
      notifications.show({
        title: "Error",
        message: error?.response?.data?.error || error?.message || "Failed to delete onboarding modal image",
        color: "red",
      });
    } finally {
      setDeletingOnboardingModalImage(false);
    }
  };

  /**
   * Handle uploading category modal top image
   */
  const handleUploadCategoryTopImage = async (file: File) => {
    if (!menu || !id) return;

    setUploadingCategoryTopImage(true);
    try {
      await uploadCategoryTopImage(id, file);
      notifications.show({
        title: "Success",
        message: "Category top image uploaded successfully",
        color: "green",
      });
      await refetch();
    } catch (error: any) {
      notifications.show({
        title: "Error",
        message: error?.response?.data?.error || error?.message || "Failed to upload category top image",
        color: "red",
      });
    } finally {
      setUploadingCategoryTopImage(false);
    }
  };

  /**
   * Handle deleting category modal top image
   */
  const handleDeleteCategoryTopImage = async () => {
    if (!menu || !id) return;

    const confirmed = window.confirm("Are you sure you want to delete the category top image?");
    if (!confirmed) return;

    setDeletingCategoryTopImage(true);
    try {
      await deleteCategoryTopImage(id);
      notifications.show({
        title: "Success",
        message: "Category top image deleted successfully",
        color: "green",
      });
      await refetch();
    } catch (error: any) {
      notifications.show({
        title: "Error",
        message: error?.response?.data?.error || error?.message || "Failed to delete category top image",
        color: "red",
      });
    } finally {
      setDeletingCategoryTopImage(false);
    }
  };

  /**
   * Handle editing a menu page field
   */
  const handlePageFieldChange = (pageId: string, field: keyof MenuPage, value: any) => {
    setEditedPages((prev) => ({
      ...prev,
      [pageId]: {
        ...prev[pageId],
        [field]: value,
      },
    }));
  };

  /**
   * Handle saving a menu page
   */
  const handleSavePage = async (pageId: string) => {
    const editedPage = editedPages[pageId];
    if (!editedPage || Object.keys(editedPage).length === 0) return;

    setSavingPageId(pageId);
    try {
      await api.put(`/menu-pages/${pageId}`, editedPage);

      // Remove from edited pages
      setEditedPages((prev) => {
        const newState = { ...prev };
        delete newState[pageId];
        return newState;
      });

      // Refetch menu data to get updated page
      await refetch();

      notifications.show({
        title: "Success",
        message: "Menu page updated successfully",
        color: "green",
      });
    } catch (e: any) {
      notifications.show({
        title: "Error",
        message: e?.response?.data?.error || e?.message || "Failed to update menu page",
        color: "red",
      });
    } finally {
      setSavingPageId(null);
    }
  };

  /**
   * Handle editing a menu item field
   */
  const handleItemFieldChange = (itemId: string, field: keyof MenuItem, value: any) => {
    setEditedItems((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [field]: value,
      },
    }));
  };

  /**
   * Handle editing nested props fields (e.g., translations.en.name.text)
   */
  const handleNestedPropsChange = (
    itemId: string,
    path: string,
    value: any,
  ) => {
    setEditedItems((prev) => {
      const currentItem = prev[itemId] || {};
      
      // Find the original item to merge with its props
      // This ensures we preserve all existing translations when editing nested fields
      const originalItem = menu?.menuPages
        ?.flatMap((page) => page.menuItems || [])
        .find((item) => item.id === itemId);
      
      // Merge original item's props with any existing edited props
      // This prevents losing category and other translations when editing name/description
      const originalProps = originalItem?.props || {};
      const currentProps = currentItem.props 
        ? { ...originalProps, ...currentItem.props }
        : originalProps;

      // Build path segments from dot notation (e.g., "translations.en.name.text")
      const pathSegments = path.split(".").filter(Boolean);

      // Deep merge helper
      const setValueAtPath = (obj: any, segments: string[], val: any): any => {
        if (segments.length === 0) return val;
        const [first, ...rest] = segments;
        return {
          ...obj,
          [first]: setValueAtPath(obj?.[first] || {}, rest, val),
        };
      };

      const updatedProps = setValueAtPath(currentProps, pathSegments, value);

      return {
        ...prev,
        [itemId]: {
          ...currentItem,
          props: updatedProps,
        },
      };
    });
  };

  /**
   * Handle item changes status update from MenuItemEditor
   */
  const handleItemChangesChange = useCallback((itemId: string, hasChanges: boolean) => {
    setItemsWithChanges((prev) => {
      const newSet = new Set(prev);
      if (hasChanges) {
        newSet.add(itemId);
      } else {
        newSet.delete(itemId);
      }
      return newSet;
    });
  }, []);

  /**
   * Handle saving a menu item
   * Now accepts updates directly from MenuItemEditor (no longer uses editedItems state)
   */
  const handleSaveItem = useCallback(async (itemId: string, updates: Partial<MenuItem>) => {
    if (!updates || Object.keys(updates).length === 0) return;

    setSavingItemId(itemId);
    try {
      // Find original item to preserve menuPageId
      const originalItem = menu?.menuPages
        ?.flatMap((page) => page.menuItems || [])
        .find((item) => item.id === itemId);

      if (!originalItem) {
        throw new Error("Original menu item not found");
      }

      // Merge edited fields with original item
      const updateData = {
        ...updates,
        menuPageId: originalItem.menuPageId, // Preserve menuPageId
      };

      await api.put(`/menu-items/${itemId}`, updateData);

      // Remove from items with changes
      setItemsWithChanges((prev) => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });

      // Refetch menu data to get updated item
      await refetch();

      notifications.show({
        title: "Success",
        message: "Menu item updated successfully",
        color: "green",
      });
    } catch (e: any) {
      notifications.show({
        title: "Error",
        message: e?.response?.data?.error || e?.message || "Failed to update menu item",
        color: "red",
      });
    } finally {
      setSavingItemId(null);
    }
  }, [menu, refetch]);

  /**
   * Handle deleting a menu item (for modal confirmation)
   */
  const handleDeleteItem = useCallback(async () => {
    if (!itemToDelete) return;

    try {
      await api.delete(`/menu-items/${itemToDelete.id}`);

      notifications.show({
        title: "Success",
        message: "Menu item deleted successfully",
        color: "green",
      });

      // Refetch menu data
      await refetch();
      setDeleteModalOpen(false);
      setItemToDelete(null);
    } catch (e: any) {
      notifications.show({
        title: "Error",
        message: e?.response?.data?.error || e?.message || "Failed to delete menu item",
        color: "red",
      });
    }
  }, [itemToDelete, refetch]);

  /**
   * Handle delete request from MenuItemEditor (sets up modal)
   */
  const handleDeleteItemRequest = useCallback((item: MenuItem) => {
    setItemToDelete(item);
    setDeleteModalOpen(true);
  }, []);

  /**
   * Handle creating a new menu item
   */
  const handleCreateItem = async (pageId: string) => {
    if (!newItemData.name || !newItemData.price) {
      notifications.show({
        title: "Error",
        message: "Name and price are required",
        color: "red",
      });
      return;
    }

    setCreatingItem(pageId);
    try {
      await api.post("/menu-items", {
        ...newItemData,
        menuPageId: pageId,
      });

      notifications.show({
        title: "Success",
        message: "Menu item created successfully",
        color: "green",
      });

      // Reset form and refetch
      setNewItemData({ name: "", description: "", price: null, category: "" });
      setCreatingItem(null);
      await refetch();
    } catch (e: any) {
      notifications.show({
        title: "Error",
        message: e?.response?.data?.error || e?.message || "Failed to create menu item",
        color: "red",
      });
    } finally {
      setCreatingItem(null);
    }
  };

  /**
   * Handle importing menu items from CSV into the current page.
   * Uses same CSV format as /admin/upload/csv: name, description, price, category, ingredients.
   */
  const handleImportCsvForPage = async () => {
    if (!menu?.id || !csvImportPageId || !csvImportFile) return;

    setCsvImportLoading(true);
    try {
      const formData = new FormData();
      formData.append("csvFile", csvImportFile);

      const response = await api.post(
        `/menus/${menu.id}/pages/${csvImportPageId}/items/import/csv`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      const { created, totalItems, errors } = response.data;
      notifications.show({
        title: "Success",
        message:
          errors > 0
            ? `Imported ${created} of ${totalItems} items (${errors} failed).`
            : `Imported ${created} menu items successfully.`,
        color: "green",
      });

      setCsvImportFile(null);
      setCsvImportPageId(null);
      await refetch();
    } catch (e: any) {
      notifications.show({
        title: "Error",
        message: e?.response?.data?.message || e?.response?.data?.error || e?.message || "Failed to import CSV",
        color: "red",
      });
    } finally {
      setCsvImportLoading(false);
    }
  };

  /**
   * Handle creating a new menu page
   */
  const handleCreatePage = async () => {
    if (!menu) return;

    setCreatingPage(true);
    try {
      await api.post("/menu-pages", {
        menuId: menu.id,
        imageUrl: newPageData.imageUrl || null,
        adminNotes: newPageData.adminNotes || null,
      });

      notifications.show({
        title: "Success",
        message: "Menu page created successfully",
        color: "green",
      });

      // Reset form and refetch
      setNewPageData({ imageUrl: "", adminNotes: "" });
      setCreatingPage(false);
      await refetch();
    } catch (e: any) {
      notifications.show({
        title: "Error",
        message: e?.response?.data?.error || e?.message || "Failed to create menu page",
        color: "red",
      });
    } finally {
      setCreatingPage(false);
    }
  };

  /**
   * Handle deleting a menu page
   */
  const handleDeletePage = async () => {
    if (!pageToDelete) return;

    try {
      await api.delete(`/menu-pages/${pageToDelete.id}`);

      notifications.show({
        title: "Success",
        message: "Menu page deleted successfully",
        color: "green",
      });

      // Refetch menu data
      await refetch();
      setDeletePageModalOpen(false);
      setPageToDelete(null);
    } catch (e: any) {
      notifications.show({
        title: "Error",
        message: e?.response?.data?.error || e?.message || "Failed to delete menu page",
        color: "red",
      });
    }
  };

  /**
   * Handle redoing analysis for all menu items
   */
  const handleRedoAnalysisAll = async () => {
    if (!menu) return;

    setRedoingAnalysis(true);
    try {
      const response = await api.post(`/menus/${menu.id}/analysis`);
      
      notifications.show({
        title: "Success",
        message: `Analysis regenerated for ${response.data.processed} items`,
        color: "green",
      });

      if (response.data.errors && response.data.errors.length > 0) {
        notifications.show({
          title: "Partial Success",
          message: `${response.data.errors.length} items failed. Check console for details.`,
          color: "yellow",
        });
      }

      await refetch();
    } catch (e: any) {
      notifications.show({
        title: "Error",
        message: e?.response?.data?.error || e?.message || "Failed to redo analysis",
        color: "red",
      });
    } finally {
      setRedoingAnalysis(false);
    }
  };

  /**
   * Handle generating FAQs for all menu items
   */
  const handleGenerateFaqsAll = async () => {
    if (!menu) return;

    setGeneratingFaqs(true);
    try {
      const response = await api.post(`/menus/${menu.id}/faqs`);
      
      notifications.show({
        title: "Success",
        message: `FAQs generated for ${response.data.processed} items`,
        color: "green",
      });

      if (response.data.errors && response.data.errors.length > 0) {
        notifications.show({
          title: "Partial Success",
          message: `${response.data.errors.length} items failed. Check console for details.`,
          color: "yellow",
        });
      }

      await refetch();
    } catch (e: any) {
      notifications.show({
        title: "Error",
        message: e?.response?.data?.error || e?.message || "Failed to generate FAQs",
        color: "red",
      });
    } finally {
      setGeneratingFaqs(false);
    }
  };

  /**
   * Handle generating a test FAQ preview for a selected menu item.
   *
   * IMPORTANT:
   * - This uses `dryRun: true` so the backend does NOT save/append to the menu item props.
   * - The returned FAQ output is displayed in a modal for review/copying.
   */
  const handleGenerateTestFaq = async () => {
    if (!menu) return;

    setGeneratingTestFaq(true);
    try {
      if (!testFaqSelectedItemId) {
        throw new Error("Please select a menu item to test.");
      }

      const response = await api.post(`/menus/${menu.id}/faqs/test`, {
        menuItemId: testFaqSelectedItemId,
        dryRun: true,
      });
      
      notifications.show({
        title: "Success",
        message: `Test FAQ preview generated for "${response.data.menuItem.name}"`,
        color: "green",
      });

      // Store the payload so we can display it in the modal (do NOT refetch; nothing was saved).
      setTestFaqResult(response.data?.menuItem ?? null);
    } catch (e: any) {
      notifications.show({
        title: "Error",
        message: e?.response?.data?.error || e?.message || "Failed to generate test FAQ",
        color: "red",
      });
    } finally {
      setGeneratingTestFaq(false);
    }
  };

  /**
   * Handle redoing analysis for a single menu item
   */
  const handleRedoAnalysisItem = useCallback(async (itemId: string) => {
    setItemAnalysisLoading((prev) => ({ ...prev, [itemId]: true }));
    try {
      const response = await api.post(`/menu-items/${itemId}/analysis`);
      
      notifications.show({
        title: "Success",
        message: `Analysis regenerated for "${response.data.menuItem.name}"`,
        color: "green",
      });

      await refetch();
    } catch (e: any) {
      notifications.show({
        title: "Error",
        message: e?.response?.data?.error || e?.message || "Failed to redo analysis",
        color: "red",
      });
    } finally {
      setItemAnalysisLoading((prev) => ({ ...prev, [itemId]: false }));
    }
  }, [refetch]);

  /**
   * Handle generating FAQ for a single menu item
   */
  const handleGenerateFaqItem = useCallback(async (itemId: string) => {
    setItemFaqLoading((prev) => ({ ...prev, [itemId]: true }));
    try {
      const response = await api.post(`/menu-items/${itemId}/faq`);
      
      notifications.show({
        title: "Success",
        message: `FAQ generated for "${response.data.menuItem.name}"`,
        color: "green",
      });

      await refetch();
    } catch (e: any) {
      notifications.show({
        title: "Error",
        message: e?.response?.data?.error || e?.message || "Failed to generate FAQ",
        color: "red",
      });
    } finally {
      setItemFaqLoading((prev) => ({ ...prev, [itemId]: false }));
    }
  }, [refetch]);

  /**
   * Handle generating an AI image for a single menu item.
   * Backend generates the image, uploads to Cloudinary, and saves imageUrl to the menu item.
   */
  const handleGenerateImageItem = useCallback(async (itemId: string) => {
    setItemImageLoading((prev) => ({ ...prev, [itemId]: true }));
    try {
      const response = await api.post(`/menu-items/${itemId}/generate-image`);

      notifications.show({
        title: "Success",
        message: `AI image generated and saved for "${response.data.menuItem?.name ?? "menu item"}"`,
        color: "green",
      });

      // Refetch menu so the item's imageUrl updates in the UI
      await refetch();
    } catch (e: any) {
      notifications.show({
        title: "Error",
        message: e?.response?.data?.error || e?.response?.data?.details || e?.message || "Failed to generate menu item image",
        color: "red",
      });
    } finally {
      setItemImageLoading((prev) => ({ ...prev, [itemId]: false }));
    }
  }, [refetch]);

  /**
   * Handle uploading a custom image for a single menu item.
   */
  const handleUploadItemImage = useCallback(async (itemId: string, file: File) => {
    setItemImageLoading((prev) => ({ ...prev, [itemId]: true }));
    try {
      await uploadMenuItemImage(itemId, file);
      notifications.show({
        title: "Success",
        message: "Menu item image uploaded successfully",
        color: "green",
      });
      await refetch();
    } catch (e: any) {
      notifications.show({
        title: "Error",
        message: e?.response?.data?.error || e?.message || "Failed to upload menu item image",
        color: "red",
      });
    } finally {
      setItemImageLoading((prev) => ({ ...prev, [itemId]: false }));
    }
  }, [refetch]);

  /**
   * Handle deleting a custom image for a single menu item.
   */
  const handleDeleteItemImage = useCallback(async (itemId: string) => {
    const confirmed = window.confirm("Are you sure you want to remove this menu item image?");
    if (!confirmed) return;

    setItemImageLoading((prev) => ({ ...prev, [itemId]: true }));
    try {
      await deleteMenuItemImage(itemId);
      notifications.show({
        title: "Success",
        message: "Menu item image removed successfully",
        color: "green",
      });
      await refetch();
    } catch (e: any) {
      notifications.show({
        title: "Error",
        message: e?.response?.data?.error || e?.message || "Failed to remove menu item image",
        color: "red",
      });
    } finally {
      setItemImageLoading((prev) => ({ ...prev, [itemId]: false }));
    }
  }, [refetch]);

  /**
   * Scan for duplicate menu items and open the review modal.
   */
  const handleOpenDedupeModal = useCallback(async () => {
    if (!menu) return;
    setDedupeScanning(true);
    try {
      const response = await api.get(`/menus/${menu.id}/dedupe`);
      const groups: DedupeGroup[] = response.data.groups ?? [];
      if (groups.length === 0) {
        notifications.show({ title: "No duplicates", message: "No items share the same name.", color: "blue" });
        return;
      }
      // Pre-select all items except the highest-scored one per group
      const preSelected = new Set<string>();
      for (const group of groups) {
        // items are already sorted best-first by the backend
        for (let i = 1; i < group.items.length; i++) {
          preSelected.add(group.items[i].id);
        }
      }
      setDedupeGroups(groups);
      setDedupeSelected(preSelected);
      setDedupeModalOpen(true);
    } catch (e: any) {
      notifications.show({
        title: "Error",
        message: e?.response?.data?.error || e?.message || "Failed to scan for duplicates",
        color: "red",
      });
    } finally {
      setDedupeScanning(false);
    }
  }, [menu]);

  /**
   * Delete the items checked in the dedupe modal.
   */
  const handleDedupeDelete = useCallback(async () => {
    if (dedupeSelected.size === 0) return;
    setDedupeDeleting(true);
    let deleted = 0;
    let failed = 0;
    for (const itemId of Array.from(dedupeSelected)) {
      try {
        await api.delete(`/menu-items/${itemId}`);
        deleted++;
      } catch {
        failed++;
      }
    }
    setDedupeDeleting(false);
    notifications.show({
      title: failed === 0 ? "Done" : "Partial success",
      message: `Deleted ${deleted} item${deleted !== 1 ? "s" : ""}${failed > 0 ? `, ${failed} failed` : ""}.`,
      color: failed > 0 ? "yellow" : "green",
    });
    setDedupeModalOpen(false);
    setDedupeGroups([]);
    setDedupeSelected(new Set());
    await refetch();
  }, [dedupeSelected, refetch]);

  /**
   * Handle saving all menu items with unsaved changes
   */
  const handleSaveAllItems = useCallback(async () => {
    if (itemsWithChanges.size === 0) {
      notifications.show({
        title: "Info",
        message: "No items with unsaved changes",
        color: "blue",
      });
      return;
    }

    const itemIds = Array.from(itemsWithChanges);
    let successCount = 0;
    let errorCount = 0;

    // Save each item sequentially to avoid overwhelming the server
    for (const itemId of itemIds) {
      const ref = itemEditorRefs.current[itemId];
      if (ref?.current) {
        try {
          await ref.current.save();
          successCount++;
        } catch (e: any) {
          errorCount++;
          console.error(`Failed to save item ${itemId}:`, e);
        }
      }
    }

    notifications.show({
      title: successCount > 0 ? (errorCount > 0 ? "Partial Success" : "Success") : "Error",
      message: `Saved ${successCount} item${successCount !== 1 ? 's' : ''}.${errorCount > 0 ? ` ${errorCount} failed.` : ""}`,
      color: errorCount > 0 ? "yellow" : "green",
    });
  }, [itemsWithChanges]);

  // Memoize derived values to prevent unnecessary recalculations
  // Must be called before any early returns (React Hooks rule)
  const qrCodeDataUrl = useMemo(() => 
    menu?.qrcode ? `data:image/png;base64,${menu.qrcode}` : null,
    [menu?.qrcode]
  );
  const menuPages = useMemo(() => menu?.menuPages || [], [menu?.menuPages]);
  const allMenuItems = useMemo(() => 
    menuPages.flatMap((page) => page.menuItems || []),
    [menuPages]
  );
  const testFaqSelectOptions = useMemo(() => 
    allMenuItems.map((item) => {
      const primaryCategory = getCategoryList(item as any, "en")[0];
      return {
        value: item.id,
        // Include category + id snippet to reduce confusion when names repeat.
        label: `${item.name}${primaryCategory ? ` — ${primaryCategory}` : ""} (${item.id.slice(0, 8)}…)`,
      };
    }),
    [allMenuItems]
  );

  if (isLoading) {
    return (
      <Group justify="center" py="xl">
        <Loader />
      </Group>
    );
  }

  if (!menu) {
    return <Text>Menu not found.</Text>;
  }

  /**
   * Get unique source-language (JP) categories from menu items
   */
  const getSourceOnlyCategories = (): string[] => {
    const categorySet = new Set<string>();
    allMenuItems.forEach((item) => {
      getSourceCategoryList(item).forEach((category) => categorySet.add(category));
    });
    return Array.from(categorySet).sort();
  };

  /**
   * Get unique English categories from menu items
   */
  const getEnglishOnlyCategories = (): string[] => {
    const categorySet = new Set<string>();
    allMenuItems.forEach((item) => {
      getEnglishCategoryList(item).forEach((category) => categorySet.add(category));
    });
    return Array.from(categorySet).sort();
  };

  /**
   * Get all unique categories from menu items (used for badge count)
   */
  const getAllCategories = (): string[] => {
    const categorySet = new Set<string>();
    allMenuItems.forEach((item) => {
      getSourceCategoryList(item).forEach((category) => categorySet.add(category));
      getEnglishCategoryList(item).forEach((category) => categorySet.add(category));
    });
    return Array.from(categorySet).sort();
  };

  /**
   * Apply categoryOrder to a set of categories
   */
  const applyOrder = (categories: string[]): string[] => {
    if (!menu.categoryOrder || !Array.isArray(menu.categoryOrder)) {
      return categories;
    }
    const orderMap = new Map<string, number>();
    menu.categoryOrder.forEach(([catName, index]: [string, number]) => {
      orderMap.set(catName, index);
    });
    return [...categories].sort((a, b) => {
      const orderA = orderMap.get(a);
      const orderB = orderMap.get(b);
      if (orderA !== undefined && orderB !== undefined) return orderA - orderB;
      if (orderA !== undefined) return -1;
      if (orderB !== undefined) return 1;
      return a.localeCompare(b);
    });
  };

  /**
   * Get ordered categories based on menu.categoryOrder
   */
  const getOrderedCategories = (): string[] => {
    return applyOrder(getAllCategories());
  };

  /**
   * Handle moving a category up or down in the order within a language-scoped list.
   * scopedCategories is the ordered list for that language group; we swap within it
   * and then rebuild the full combined order.
   */
  const handleMoveCategory = async (categoryName: string, direction: 'up' | 'down', scopedCategories: string[]) => {
    if (!menu) return;

    const currentIndex = scopedCategories.indexOf(categoryName);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= scopedCategories.length) return;

    const newScoped = [...scopedCategories];
    [newScoped[currentIndex], newScoped[newIndex]] = [newScoped[newIndex], newScoped[currentIndex]];

    // Rebuild full order: preserve the order of the other group, then append this group's new order
    const otherGroupCategories = getOrderedCategories().filter((c) => !scopedCategories.includes(c));
    await handleCategoryOrderChange([...otherGroupCategories, ...newScoped]);
  };

  /**
   * Persist a new category order (used by both up/down buttons and drag-and-drop).
   * Builds categoryOrder array [[categoryName, index], ...] and updates the menu.
   */
  const handleCategoryOrderChange = async (newOrder: string[]) => {
    if (!menu) return;

    const categoryOrder = newOrder.map((cat, idx) => [cat, idx]);

    try {
      await updateMenuAsync({
        resource: "menus",
        id: menu.id,
        values: { categoryOrder },
      });

      notifications.show({
        title: "Success",
        message: "Category order updated successfully",
        color: "green",
      });

      await refetch();
    } catch (e: any) {
      notifications.show({
        title: "Error",
        message: (e as any)?.message ?? "Failed to update category order",
        color: "red",
      });
    }
  };

  /**
   * Handle downloading menu items as CSV
   */
  const handleDownloadCSV = async () => {
    if (!menu || !id) return;

    try {
      // Fetch CSV from the backend endpoint
      const response = await api.get(`/menus/${id}/export/csv`, {
        responseType: 'blob', // Important: tell axios to handle binary data
      });

      // Create a blob from the response
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      
      // Create a temporary URL for the blob
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary anchor element and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `menu-items-${id}-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      notifications.show({
        title: "Success",
        message: "CSV file downloaded successfully",
        color: "green",
      });
    } catch (error: any) {
      console.error('Error downloading CSV:', error);
      notifications.show({
        title: "Error",
        message: error?.response?.data?.error || error?.message || "Failed to download CSV",
        color: "red",
      });
    }
  };

  /**
   * Handle bulk renaming a category
   */
  const handleRenameCategory = async () => {
    if (!menu || !categoryToRename || !newCategoryName.trim()) return;

    if (categoryToRename === newCategoryName.trim()) {
      notifications.show({
        title: "Error",
        message: "New category name must be different from the old name",
        color: "red",
      });
      return;
    }

    setRenamingCategory(true);
    try {
      const response = await api.post(`/menus/${menu.id}/categories/rename`, {
        oldCategoryName: categoryToRename,
        newCategoryName: newCategoryName.trim(),
      });

      notifications.show({
        title: "Success",
        message: `Category renamed. ${response.data.updated} menu items updated.`,
        color: "green",
      });

      // Close modal and reset state
      setCategoryRenameModalOpen(false);
      setCategoryToRename(null);
      setNewCategoryName("");
      
      // Refetch menu data
      await refetch();
    } catch (e: any) {
      notifications.show({
        title: "Error",
        message: e?.response?.data?.error || e?.message || "Failed to rename category",
        color: "red",
      });
    } finally {
      setRenamingCategory(false);
    }
  };

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={2}>Edit menu</Title>
        <Group>
          <Button
            variant="filled"
            color="orange"
            component={Link}
            to={`/admin/menus/edit/v2/${menu.id}`}
            leftSection={<IconWand size={16} />}
          >
            Try v2 editor
          </Button>
          <Button
            variant="light"
            leftSection={<IconDownload size={16} />}
            onClick={handleDownloadCSV}
          >
            Download CSV
          </Button>
          <Button variant="default" component={Link} to={`/menus/v2/${menu.id}`}>
            View public menu
          </Button>
          <Button variant="default" onClick={() => navigate("/admin/menus")}>
            Back to menus
          </Button>
        </Group>
      </Group>

      {error ? <Alert color="red">{error}</Alert> : null}

      {/* Menu Info Card */}
      <Card withBorder>
        <Stack gap="xs">
          <Group justify="space-between" align="flex-start">
            <Stack gap="xs">
              <Text fw={600}>Restaurant</Text>
              <Text c="dimmed">{menu.restaurant?.name ?? menu.restaurantId}</Text>
            </Stack>
            {menu.restaurantId && (
              <Button
                variant="light"
                component={Link}
                to={`/admin/restaurants/edit/${menu.restaurantId}`}
                size="sm"
              >
                Edit Restaurant
              </Button>
            )}
          </Group>
          <Text fw={600} mt="sm">
            Menu ID
          </Text>
          <Text c="dimmed" style={{ fontFamily: "monospace" }}>
            {menu.id}
          </Text>

          <Text fw={600} mt="sm">
            QR code
          </Text>
          {qrCodeDataUrl ? (
            <Image src={qrCodeDataUrl} alt="Menu QR code" w={200} fit="contain" />
          ) : (
            <Text size="sm" c="dimmed">
              No QR code yet. Generate one so customers can scan to open this menu.
            </Text>
          )}
          {menu.imageUrl ? (
            <>
              <Text fw={600} mt="sm">
                Menu image
              </Text>
              <Image src={menu.imageUrl} alt="Menu image" maw={360} fit="contain" />
            </>
          ) : null}
        </Stack>
      </Card>

      {/* Tabs for Menu Metadata, Pages, and Items */}
      <Tabs defaultValue="metadata">
        <Tabs.List>
          <Tabs.Tab value="metadata">Menu Metadata</Tabs.Tab>
          <Tabs.Tab value="categories">
            Categories
            {getOrderedCategories().length > 0 && (
              <Badge size="sm" variant="light" ml="xs">
                {getOrderedCategories().length}
              </Badge>
            )}
          </Tabs.Tab>
          <Tabs.Tab value="ai-actions">
            AI Actions
            <IconWand size={16} style={{ marginLeft: 8 }} />
          </Tabs.Tab>
          <Tabs.Tab value="pages">
            Menu Pages
            {menuPages.length > 0 && (
              <Badge size="sm" variant="light" ml="xs">
                {menuPages.length}
              </Badge>
            )}
          </Tabs.Tab>
        </Tabs.List>

        {/* Menu Metadata Tab */}
        <Tabs.Panel value="metadata" pt="md">
          <Stack gap="md">
            <Textarea
              label="Admin notes"
              minRows={4}
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.currentTarget.value)}
            />

            {/* Menu Header Image Section */}
            <Card withBorder p="md">
              <Stack gap="md">
                <Title order={5}>Menu Header Image</Title>
                <Text size="sm" c="dimmed">
                  This image appears at the top of the menu page as a header/hero image.
                </Text>
                <Text size="xs" c="dimmed">
                  Recommended dimensions: <Code>{IMAGE_DIMENSION_GUIDANCE.menuHero}</Code>
                </Text>
                
                {menu.imageUrl ? (
                  <Stack gap="sm">
                    <Image src={menu.imageUrl} alt="Menu header image" maw={400} fit="contain" />
                    <Group>
                      <label>
                        <input
                          type="file"
                          accept="image/*"
                          style={{ display: 'none' }}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              handleUploadMenuHeaderImage(file);
                            }
                          }}
                          disabled={uploadingMenuHeaderImage}
                        />
                        <Button
                          component="span"
                          variant="light"
                          loading={uploadingMenuHeaderImage}
                          disabled={uploadingMenuHeaderImage || deletingMenuHeaderImage}
                        >
                          Replace Image
                        </Button>
                      </label>
                      <Button
                        color="red"
                        variant="light"
                        onClick={handleDeleteMenuHeaderImage}
                        loading={deletingMenuHeaderImage}
                        disabled={uploadingMenuHeaderImage || deletingMenuHeaderImage}
                        leftSection={<IconTrash size={16} />}
                      >
                        Remove Image
                      </Button>
                    </Group>
                  </Stack>
                ) : (
                  <label>
                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          handleUploadMenuHeaderImage(file);
                        }
                      }}
                      disabled={uploadingMenuHeaderImage}
                    />
                    <Button
                      component="span"
                      variant="light"
                      loading={uploadingMenuHeaderImage}
                      disabled={uploadingMenuHeaderImage}
                    >
                      Upload Menu Header Image
                    </Button>
                  </label>
                )}
              </Stack>
            </Card>

            {/* Category Top Image Section */}
            <Card withBorder p="md">
              <Stack gap="md">
                <Title order={5}>Category Top Image</Title>
                <Text size="sm" c="dimmed">
                  This image appears at the top of the category selection modal shown to customers.
                </Text>
                <Text size="xs" c="dimmed">
                  Recommended dimensions: <Code>{IMAGE_DIMENSION_GUIDANCE.categoryTop}</Code>
                </Text>

                {menu.categoryTopImageUrl ? (
                  <Stack gap="sm">
                    <Image src={menu.categoryTopImageUrl} alt="Category top image" maw={400} fit="contain" />
                    <Group>
                      <label>
                        <input
                          type="file"
                          accept="image/*"
                          style={{ display: "none" }}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              handleUploadCategoryTopImage(file);
                            }
                          }}
                          disabled={uploadingCategoryTopImage}
                        />
                        <Button
                          component="span"
                          variant="light"
                          loading={uploadingCategoryTopImage}
                          disabled={uploadingCategoryTopImage || deletingCategoryTopImage}
                        >
                          Replace Image
                        </Button>
                      </label>
                      <Button
                        color="red"
                        variant="light"
                        onClick={handleDeleteCategoryTopImage}
                        loading={deletingCategoryTopImage}
                        disabled={uploadingCategoryTopImage || deletingCategoryTopImage}
                        leftSection={<IconTrash size={16} />}
                      >
                        Remove Image
                      </Button>
                    </Group>
                  </Stack>
                ) : (
                  <label>
                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          handleUploadCategoryTopImage(file);
                        }
                      }}
                      disabled={uploadingCategoryTopImage}
                    />
                    <Button
                      component="span"
                      variant="light"
                      loading={uploadingCategoryTopImage}
                      disabled={uploadingCategoryTopImage}
                    >
                      Upload Category Top Image
                    </Button>
                  </label>
                )}
              </Stack>
            </Card>

            {/* Onboarding Modal Image Section */}
            <Card withBorder p="md">
              <Stack gap="md">
                <Title order={5}>Onboarding Modal Image</Title>
                <Text size="sm" c="dimmed">
                  This image appears in the welcome/onboarding modal that customers see when they first view the menu.
                </Text>
                <Text size="xs" c="dimmed">
                  Recommended dimensions: <Code>{IMAGE_DIMENSION_GUIDANCE.onboardingModal}</Code>
                </Text>
                
                {menu.onboardingModalImageUrl ? (
                  <Stack gap="sm">
                    <Image src={menu.onboardingModalImageUrl} alt="Onboarding modal image" maw={400} fit="contain" />
                    <Group>
                      <label>
                        <input
                          type="file"
                          accept="image/*"
                          style={{ display: 'none' }}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              handleUploadOnboardingModalImage(file);
                            }
                          }}
                          disabled={uploadingOnboardingModalImage}
                        />
                        <Button
                          component="span"
                          variant="light"
                          loading={uploadingOnboardingModalImage}
                          disabled={uploadingOnboardingModalImage || deletingOnboardingModalImage}
                        >
                          Replace Image
                        </Button>
                      </label>
                      <Button
                        color="red"
                        variant="light"
                        onClick={handleDeleteOnboardingModalImage}
                        loading={deletingOnboardingModalImage}
                        disabled={uploadingOnboardingModalImage || deletingOnboardingModalImage}
                        leftSection={<IconTrash size={16} />}
                      >
                        Remove Image
                      </Button>
                    </Group>
                  </Stack>
                ) : (
                  <label>
                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          handleUploadOnboardingModalImage(file);
                        }
                      }}
                      disabled={uploadingOnboardingModalImage}
                    />
                    <Button
                      component="span"
                      variant="light"
                      loading={uploadingOnboardingModalImage}
                      disabled={uploadingOnboardingModalImage}
                    >
                      Upload Onboarding Modal Image
                    </Button>
                  </label>
                )}
              </Stack>
            </Card>

            <Textarea
              label="Category order (JSON)"
              description="Optional. Must be valid JSON if provided."
              minRows={8}
              value={categoryOrderText}
              onChange={(e) => setCategoryOrderText(e.currentTarget.value)}
            />

            <Textarea
              label="Query setup (JSON)"
              description="Optional. Must be valid JSON if provided."
              minRows={10}
              value={querySetupText}
              onChange={(e) => setQuerySetupText(e.currentTarget.value)}
            />

            {/* Drinks Section */}
            <Card withBorder p="md">
              <Stack gap="md">
                <div>
                  <Title order={4}>Drinks Section</Title>
                  <Text size="sm" c="dimmed">
                    Show a Food / Drinks tab switcher on the public menu. Off by default. When enabled, assign categories to the Drinks tab — all others remain under Food.
                  </Text>
                </div>
                <Switch
                  label="Enable Food / Drinks tabs"
                  checked={drinkSectionEnabled}
                  onChange={(e) => setDrinkSectionEnabled(e.currentTarget.checked)}
                />
                {drinkSectionEnabled && (
                  <MultiSelect
                    label="Drinks categories"
                    description="Select which categories belong to the Drinks tab. All other categories will appear under Food."
                    placeholder="Pick categories…"
                    data={getAllCategories()}
                    value={drinkCategories}
                    onChange={setDrinkCategories}
                    searchable
                    clearable
                  />
                )}
              </Stack>
            </Card>

            {/* Welcome Modal Customization Section */}
            <Card withBorder p="md">
              <Stack gap="md">
                <Group justify="space-between" align="flex-start">
                  <div>
                    <Title order={4}>Welcome Modal Customization</Title>
                    <Text size="sm" c="dimmed">
                      Customize the welcome modal text and question chips that appear when customers first view the menu.
                      Use {'{restaurantName}'} as a placeholder for the restaurant name in titles.
                    </Text>
                  </div>
                  <Button
                    leftSection={<IconWand size={16} />}
                    onClick={handleGenerateOnboardingContent}
                    loading={generatingOnboardingContent}
                    variant="light"
                    color="blue"
                  >
                    Generate with AI
                  </Button>
                </Group>

                <Tabs defaultValue="en">
                  <Tabs.List>
                    <Tabs.Tab value="en">English</Tabs.Tab>
                    <Tabs.Tab value="jp">Japanese</Tabs.Tab>
                    <Tabs.Tab value="fallback">Fallback</Tabs.Tab>
                  </Tabs.List>

                  <Tabs.Panel value="en" pt="md">
                    <Stack gap="md">
                      <TextInput
                        label="Modal Title"
                        placeholder="Welcome to {restaurantName}!"
                        value={onboardingContent.languageConfigs?.en?.title || ''}
                        onChange={(e) => setOnboardingContent(prev => ({
                          ...prev,
                          languageConfigs: {
                            ...prev.languageConfigs,
                            en: {
                              ...prev.languageConfigs?.en,
                              title: e.target.value || undefined
                            }
                          }
                        }))}
                        description="Use {restaurantName} as placeholder for restaurant name"
                      />
                      <Textarea
                        label="Section 1 Title"
                        placeholder="Menu Assistant 🤖"
                        value={onboardingContent.languageConfigs?.en?.sections?.[0]?.title || ''}
                        onChange={(e) => updateOnboardingSection('en', 0, 'title', e.target.value)}
                      />
                      <Textarea
                        label="Section 1 Content"
                        placeholder="Our AI assistant can answer questions about menu items, and provide recommendations."
                        value={onboardingContent.languageConfigs?.en?.sections?.[0]?.content || ''}
                        onChange={(e) => updateOnboardingSection('en', 0, 'content', e.target.value)}
                        autosize
                        minRows={3}
                      />
                      <Textarea
                        label="Section 2 Title"
                        placeholder="Easy Ordering 🍽️"
                        value={onboardingContent.languageConfigs?.en?.sections?.[1]?.title || ''}
                        onChange={(e) => updateOnboardingSection('en', 1, 'title', e.target.value)}
                      />
                      <Textarea
                        label="Section 2 Content"
                        placeholder="Add items you want from the menu. Once you're ready, show the shop staff the order screen to place your order.👌"
                        value={onboardingContent.languageConfigs?.en?.sections?.[1]?.content || ''}
                        onChange={(e) => updateOnboardingSection('en', 1, 'content', e.target.value)}
                        autosize
                        minRows={3}
                      />
                      <TextInput
                        label="Button Text"
                        placeholder="Get Started 🍱🍜🍣🥢"
                        value={onboardingContent.languageConfigs?.en?.buttonText || ''}
                        onChange={(e) => setOnboardingContent(prev => ({
                          ...prev,
                          languageConfigs: {
                            ...prev.languageConfigs,
                            en: {
                              ...prev.languageConfigs?.en,
                              buttonText: e.target.value || undefined
                            }
                          }
                        }))}
                      />
                      <Textarea
                        label="Question Chips (one per line)"
                        placeholder="What should I order to start?&#10;Give me some japanese phrases I can use."
                        value={onboardingContent.languageConfigs?.en?.questionChips?.join('\n') || ''}
                        onChange={(e) => setOnboardingContent(prev => ({
                          ...prev,
                          languageConfigs: {
                            ...prev.languageConfigs,
                            en: {
                              ...prev.languageConfigs?.en,
                              questionChips: e.target.value ? e.target.value.split('\n').filter(line => line.trim()) : undefined
                            }
                          }
                        }))}
                        autosize
                        minRows={3}
                        description="Enter one question per line. Leave empty to use default questions."
                      />
                    </Stack>
                  </Tabs.Panel>

                  <Tabs.Panel value="jp" pt="md">
                    <Stack gap="md">
                      <TextInput
                        label="Modal Title"
                        placeholder="ようこそ {restaurantName}へ！"
                        value={onboardingContent.languageConfigs?.jp?.title || ''}
                        onChange={(e) => setOnboardingContent(prev => ({
                          ...prev,
                          languageConfigs: {
                            ...prev.languageConfigs,
                            jp: {
                              ...prev.languageConfigs?.jp,
                              title: e.target.value || undefined
                            }
                          }
                        }))}
                        description="Use {restaurantName} as placeholder for restaurant name"
                      />
                      <Textarea
                        label="Section 1 Title"
                        placeholder="はるちゃんとチャット！🤖"
                        value={onboardingContent.languageConfigs?.jp?.sections?.[0]?.title || ''}
                        onChange={(e) => updateOnboardingSection('jp', 0, 'title', e.target.value)}
                      />
                      <Textarea
                        label="Section 1 Content"
                        placeholder="AIアシスタントがメニューアイテムについて質問に答え、おすすめを提供します。"
                        value={onboardingContent.languageConfigs?.jp?.sections?.[0]?.content || ''}
                        onChange={(e) => updateOnboardingSection('jp', 0, 'content', e.target.value)}
                        autosize
                        minRows={3}
                      />
                      <Textarea
                        label="Section 2 Title"
                        placeholder="簡単注文 🍽️"
                        value={onboardingContent.languageConfigs?.jp?.sections?.[1]?.title || ''}
                        onChange={(e) => updateOnboardingSection('jp', 1, 'title', e.target.value)}
                      />
                      <Textarea
                        label="Section 2 Content"
                        placeholder="メニューから欲しいアイテムを追加してください。準備ができたら、注文画面を店員に見せて注文してください。👌"
                        value={onboardingContent.languageConfigs?.jp?.sections?.[1]?.content || ''}
                        onChange={(e) => updateOnboardingSection('jp', 1, 'content', e.target.value)}
                        autosize
                        minRows={3}
                      />
                      <TextInput
                        label="Button Text"
                        placeholder="始めましょう 🍱🍜🍣🥢"
                        value={onboardingContent.languageConfigs?.jp?.buttonText || ''}
                        onChange={(e) => setOnboardingContent(prev => ({
                          ...prev,
                          languageConfigs: {
                            ...prev.languageConfigs,
                            jp: {
                              ...prev.languageConfigs?.jp,
                              buttonText: e.target.value || undefined
                            }
                          }
                        }))}
                      />
                      <Textarea
                        label="Question Chips (one per line)"
                        placeholder="何から注文すればいいですか？&#10;使える日本語のフレーズを教えてください。"
                        value={onboardingContent.languageConfigs?.jp?.questionChips?.join('\n') || ''}
                        onChange={(e) => setOnboardingContent(prev => ({
                          ...prev,
                          languageConfigs: {
                            ...prev.languageConfigs,
                            jp: {
                              ...prev.languageConfigs?.jp,
                              questionChips: e.target.value ? e.target.value.split('\n').filter(line => line.trim()) : undefined
                            }
                          }
                        }))}
                        autosize
                        minRows={3}
                        description="Enter one question per line. Leave empty to use default questions."
                      />
                    </Stack>
                  </Tabs.Panel>

                  <Tabs.Panel value="fallback" pt="md">
                    <Stack gap="md">
                      <Text size="sm" c="dimmed">
                        This content will be used when no language-specific configuration is available.
                      </Text>
                      <TextInput
                        label="Modal Title"
                        placeholder="Welcome to {restaurantName}!"
                        value={onboardingContent.fallbackConfig?.title || ''}
                        onChange={(e) => setOnboardingContent(prev => ({
                          ...prev,
                          fallbackConfig: {
                            ...prev.fallbackConfig,
                            title: e.target.value || undefined
                          }
                        }))}
                        description="Use {restaurantName} as placeholder for restaurant name"
                      />
                      <Textarea
                        label="Section 1 Title"
                        placeholder="Menu Assistant 🤖"
                        value={onboardingContent.fallbackConfig?.sections?.[0]?.title || ''}
                        onChange={(e) => updateOnboardingSection('fallback', 0, 'title', e.target.value)}
                      />
                      <Textarea
                        label="Section 1 Content"
                        placeholder="Our AI assistant can answer questions about menu items, and provide recommendations."
                        value={onboardingContent.fallbackConfig?.sections?.[0]?.content || ''}
                        onChange={(e) => updateOnboardingSection('fallback', 0, 'content', e.target.value)}
                        autosize
                        minRows={3}
                      />
                      <Textarea
                        label="Section 2 Title"
                        placeholder="Easy Ordering 🍽️"
                        value={onboardingContent.fallbackConfig?.sections?.[1]?.title || ''}
                        onChange={(e) => updateOnboardingSection('fallback', 1, 'title', e.target.value)}
                      />
                      <Textarea
                        label="Section 2 Content"
                        placeholder="Add items you want from the menu. Once you're ready, show the shop staff the order screen to place your order.👌"
                        value={onboardingContent.fallbackConfig?.sections?.[1]?.content || ''}
                        onChange={(e) => updateOnboardingSection('fallback', 1, 'content', e.target.value)}
                        autosize
                        minRows={3}
                      />
                      <TextInput
                        label="Button Text"
                        placeholder="Get Started 🍱🍜🍣🥢"
                        value={onboardingContent.fallbackConfig?.buttonText || ''}
                        onChange={(e) => setOnboardingContent(prev => ({
                          ...prev,
                          fallbackConfig: {
                            ...prev.fallbackConfig,
                            buttonText: e.target.value || undefined
                          }
                        }))}
                      />
                      <Textarea
                        label="Question Chips (one per line)"
                        placeholder="What should I order to start?&#10;Give me some japanese phrases I can use."
                        value={onboardingContent.fallbackConfig?.questionChips?.join('\n') || ''}
                        onChange={(e) => setOnboardingContent(prev => ({
                          ...prev,
                          fallbackConfig: {
                            ...prev.fallbackConfig,
                            questionChips: e.target.value ? e.target.value.split('\n').filter(line => line.trim()) : undefined
                          }
                        }))}
                        autosize
                        minRows={3}
                        description="Enter one question per line. Leave empty to use default questions."
                      />
                    </Stack>
                  </Tabs.Panel>
                </Tabs>
              </Stack>
            </Card>

            <Group justify="flex-end">
              <Button loading={isSavingMenu} onClick={handleSaveMenu}>
                Save Menu Metadata
              </Button>
            </Group>
          </Stack>
        </Tabs.Panel>

        {/* AI Actions Tab */}
        <Tabs.Panel value="ai-actions" pt="md">
          <Stack gap="lg">
            <Card withBorder p="lg" style={{ backgroundColor: "#f8f9fa" }}>
              <Stack gap="md">
                <Group justify="space-between" align="flex-start">
                  <div>
                    <Title order={4} mb="xs">Menu-Level AI Actions</Title>
                    <Text size="sm" c="dimmed">
                      Regenerate AI analysis and FAQs for all menu items. These operations may take several minutes.
                    </Text>
                  </div>
                  <Badge size="lg" variant="light" color="green">
                    {menuPages.reduce((count, page) => count + (page.menuItems?.length || 0), 0)} items
                  </Badge>
                </Group>
                
                <Group gap="md" mt="md">
                  <Button
                    leftSection={<IconRefresh size={18} />}
                    onClick={handleRedoAnalysisAll}
                    loading={redoingAnalysis}
                    variant="light"
                    color="green"
                    size="md"
                  >
                    Redo Analysis (All Items)
                  </Button>
                  
                  <Button
                    leftSection={<IconQuestionMark size={18} />}
                    onClick={handleGenerateFaqsAll}
                    loading={generatingFaqs}
                    variant="light"
                    color="green"
                    size="md"
                  >
                    Generate FAQs (All Items)
                  </Button>
                  
                  <Button
                    leftSection={<IconWand size={18} />}
                    onClick={() => {
                      // Default selection to first item if nothing is selected yet.
                      if (!testFaqSelectedItemId && allMenuItems.length > 0) {
                        setTestFaqSelectedItemId(allMenuItems[0].id);
                      }
                      setTestFaqResult(null);
                      setTestFaqModalOpen(true);
                    }}
                    loading={generatingTestFaq}
                    variant="light"
                    color="orange"
                    size="md"
                  >
                    Generate Test FAQ
                  </Button>

                  <Button
                    leftSection={<IconX size={18} />}
                    onClick={handleOpenDedupeModal}
                    loading={dedupeScanning}
                    variant="light"
                    color="red"
                    size="md"
                  >
                    Dedupe Items
                  </Button>
                </Group>
              </Stack>
            </Card>

            <Card withBorder p="lg">
              <Stack gap="md">
                <Title order={4}>What These Actions Do</Title>
                <Stack gap="md">
                  <div>
                    <Text size="sm" fw={600} mb={4}>
                      <IconRefresh size={14} style={{ marginRight: 8, verticalAlign: "middle" }} />
                      Redo Analysis
                    </Text>
                    <Text size="sm" c="dimmed" ml={22}>
                      Regenerates ingredient analysis, dietary information, allergens, and other structured data for menu items using AI. This updates the analysis stored in each item's props.
                    </Text>
                  </div>
                  <Divider />
                  <div>
                    <Text size="sm" fw={600} mb={4}>
                      <IconQuestionMark size={14} style={{ marginRight: 8, verticalAlign: "middle" }} />
                      Generate FAQs
                    </Text>
                    <Text size="sm" c="dimmed" ml={22}>
                      Creates frequently asked questions and answers for menu items to help customers understand dishes better. FAQs are stored in each item's props and displayed on the public menu.
                    </Text>
                  </div>
                  <Divider />
                  <div>
                    <Text size="sm" fw={600} mb={4}>
                      <IconWand size={14} style={{ marginRight: 8, verticalAlign: "middle" }} />
                      Generate Test FAQ
                    </Text>
                    <Text size="sm" c="dimmed" ml={22}>
                      Opens a preview modal where you can choose a menu item and generate a test FAQ output without saving it to the menu. Useful for validating prompt quality before running FAQ generation on all items.
                    </Text>
                  </div>
                  <Divider />
                  <div>
                    <Text size="sm" fw={600} mb={4}>
                      <IconX size={14} style={{ marginRight: 8, verticalAlign: "middle" }} />
                      Dedupe Items
                    </Text>
                    <Text size="sm" c="dimmed" ml={22}>
                      Finds menu items with identical names and removes duplicates, keeping the copy with the richest data (longer description, has image, has AI analysis). Useful after re-uploads that create duplicate entries.
                    </Text>
                  </div>
                </Stack>
              </Stack>
            </Card>

            <Alert color="green" title="Note">
              <Text size="sm">
                Individual item actions are available in the "Advanced" tab of each menu item. Use menu-level actions to process all items at once.
              </Text>
            </Alert>
          </Stack>
        </Tabs.Panel>

        {/* Categories Tab */}
        <Tabs.Panel value="categories" pt="md">
          <Stack gap="md">
            <Card withBorder p="md">
              <Stack gap="md">
                <Title order={4}>Category Management</Title>
                <Text size="sm" c="dimmed">
                  Drag categories to reorder. Order is used on the public menu. You can also use the arrows or rename a category (renaming updates all menu items with that category).
                </Text>

                {(() => {
                  const orderedSourceCategories = applyOrder(getSourceOnlyCategories());
                  const orderedEnCategories = applyOrder(getEnglishOnlyCategories());

                  if (orderedSourceCategories.length === 0 && orderedEnCategories.length === 0) {
                    return (
                      <Text c="dimmed" mt="md">
                        No categories found in this menu.
                      </Text>
                    );
                  }

                  const renderCategoryList = (categories: string[], droppableId: string) => {
                    if (categories.length === 0) {
                      return <Text size="sm" c="dimmed">No categories.</Text>;
                    }

                    const handleDragEnd = (result: DropResult) => {
                      if (result.destination == null || result.destination.index === result.source.index) return;
                      const newScoped = Array.from(categories);
                      const [removed] = newScoped.splice(result.source.index, 1);
                      newScoped.splice(result.destination.index, 0, removed);
                      const otherGroupCategories = getOrderedCategories().filter((c) => !categories.includes(c));
                      handleCategoryOrderChange([...otherGroupCategories, ...newScoped]);
                    };

                    return (
                      <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId={droppableId}>
                          {(provided) => (
                            <Stack
                              gap="xs"
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                            >
                              {categories.map((category, index) => {
                                const itemCount = allMenuItems.filter((item) => itemHasCategory(item, category)).length;

                                return (
                                  <Draggable key={category} draggableId={category} index={index}>
                                    {(provided, snapshot) => (
                                      <Card
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        withBorder
                                        p="sm"
                                        style={{
                                          ...provided.draggableProps.style,
                                          backgroundColor: snapshot.isDragging ? "var(--mantine-color-default-hover)" : undefined,
                                        }}
                                      >
                                        <Group justify="space-between" wrap="nowrap">
                                          <Group gap="xs" style={{ flex: 1 }} wrap="nowrap">
                                            <Box
                                              {...provided.dragHandleProps}
                                              style={{ cursor: "grab", touchAction: "none" }}
                                              aria-label="Drag to reorder"
                                            >
                                              <IconGripVertical size={18} color="var(--mantine-color-dimmed)" />
                                            </Box>
                                            <Text fw={500} style={{ minWidth: 0, flex: 1 }}>
                                              {category}
                                            </Text>
                                            <Badge size="sm" variant="light" color="blue">
                                              {itemCount} {itemCount === 1 ? "item" : "items"}
                                            </Badge>
                                          </Group>
                                          <Group gap="xs" wrap="nowrap">
                                            <ActionIcon
                                              variant="subtle"
                                              color="gray"
                                              onClick={() => handleMoveCategory(category, "up", categories)}
                                              disabled={index === 0}
                                              title="Move up"
                                            >
                                              <IconArrowUp size={16} />
                                            </ActionIcon>
                                            <ActionIcon
                                              variant="subtle"
                                              color="gray"
                                              onClick={() => handleMoveCategory(category, "down", categories)}
                                              disabled={index === categories.length - 1}
                                              title="Move down"
                                            >
                                              <IconArrowDown size={16} />
                                            </ActionIcon>
                                            <ActionIcon
                                              variant="subtle"
                                              color="blue"
                                              onClick={() => {
                                                setCategoryToRename(category);
                                                setNewCategoryName(category);
                                                setCategoryRenameModalOpen(true);
                                              }}
                                              title="Rename category"
                                            >
                                              <IconEdit size={16} />
                                            </ActionIcon>
                                          </Group>
                                        </Group>
                                      </Card>
                                    )}
                                  </Draggable>
                                );
                              })}
                              {provided.placeholder}
                            </Stack>
                          )}
                        </Droppable>
                      </DragDropContext>
                    );
                  };

                  return (
                    <Stack gap="xl" mt="md">
                      <Stack gap="xs">
                        <Group gap="xs" align="center">
                          <Text size="sm" fw={600}>Source / Japanese</Text>
                          <Badge size="sm" variant="outline" color="gray">{orderedSourceCategories.length}</Badge>
                        </Group>
                        {renderCategoryList(orderedSourceCategories, "category-order-source")}
                      </Stack>

                      <Divider />

                      <Stack gap="xs">
                        <Group gap="xs" align="center">
                          <Text size="sm" fw={600}>English</Text>
                          <Badge size="sm" variant="outline" color="gray">{orderedEnCategories.length}</Badge>
                        </Group>
                        {renderCategoryList(orderedEnCategories, "category-order-en")}
                      </Stack>
                    </Stack>
                  );
                })()}
              </Stack>
            </Card>
          </Stack>
        </Tabs.Panel>

        {/* Menu Pages Tab */}
        <Tabs.Panel value="pages" pt="md">
          <Stack gap="md">
            {/* Create New Menu Page Section */}
            <Card withBorder p="md">
              <Stack gap="md">
                <Group justify="space-between">
                  <Title order={4}>Create New Menu Page</Title>
                  <Button
                    leftSection={<IconPlus size={16} />}
                    onClick={() => setCreatingPage(!creatingPage)}
                    variant="light"
                  >
                    {creatingPage ? "Cancel" : "New Page"}
                  </Button>
                </Group>

                {creatingPage && (
                  <Stack gap="md">
                    <TextInput
                      label="Image URL"
                      value={newPageData.imageUrl || ""}
                      onChange={(e) =>
                        setNewPageData({ ...newPageData, imageUrl: e.currentTarget.value })
                      }
                      placeholder="Enter Cloudinary URL or image URL"
                    />
                    <Textarea
                      label="Admin notes"
                      minRows={3}
                      value={newPageData.adminNotes || ""}
                      onChange={(e) =>
                        setNewPageData({ ...newPageData, adminNotes: e.currentTarget.value })
                      }
                    />
                    <Group justify="flex-end">
                      <Button
                        loading={creatingPage}
                        onClick={handleCreatePage}
                        leftSection={<IconPlus size={16} />}
                      >
                        Create Page
                      </Button>
                    </Group>
                  </Stack>
                )}
              </Stack>
            </Card>

            {menuPages.length === 0 ? (
              <Text c="dimmed">No menu pages found. Create one above.</Text>
            ) : (
              menuPages.map((page) => {
                const editedPage = editedPages[page.id] || {};
                const hasChanges = Object.keys(editedPage).length > 0;
                const isSaving = savingPageId === page.id;

                return (
                  <Card key={page.id} withBorder>
                    <Stack gap="md">
                      <Group justify="space-between">
                        <Title order={4}>Menu Page {page.id.slice(0, 8)}...</Title>
                        <Group>
                          {hasChanges && (
                            <Badge color="yellow" variant="light">
                              Unsaved changes
                            </Badge>
                          )}
                          <ActionIcon
                            color="red"
                            variant="subtle"
                            onClick={() => {
                              setPageToDelete(page);
                              setDeletePageModalOpen(true);
                            }}
                            title="Delete menu page"
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Group>
                      </Group>

                      {page.imageUrl && (
                        <Image src={page.imageUrl} alt="Menu page" maw={400} fit="contain" />
                      )}

                      <TextInput
                        label="Image URL"
                        value={(editedPage.imageUrl ?? page.imageUrl) || ""}
                        onChange={(e) =>
                          handlePageFieldChange(page.id, "imageUrl", e.currentTarget.value)
                        }
                        placeholder="Enter Cloudinary URL or image URL"
                      />

                      <Textarea
                        label="Admin notes"
                        minRows={3}
                        value={(editedPage.adminNotes ?? page.adminNotes) || ""}
                        onChange={(e) =>
                          handlePageFieldChange(page.id, "adminNotes", e.currentTarget.value)
                        }
                      />

                      <Group justify="flex-end">
                        {hasChanges && (
                          <Button
                            variant="subtle"
                            onClick={() => {
                              setEditedPages((prev) => {
                                const newState = { ...prev };
                                delete newState[page.id];
                                return newState;
                              });
                            }}
                          >
                            Discard
                          </Button>
                        )}
                        <Button
                          loading={isSaving}
                          disabled={!hasChanges}
                          onClick={() => handleSavePage(page.id)}
                        >
                          Save Page
                        </Button>
                      </Group>

                      <Divider />

                      {/* Menu Items for this page */}
                      <Stack gap="xs">
                        <Group justify="space-between">
                          <Text fw={600}>Menu Items ({page.menuItems?.length || 0})</Text>
                          <Group gap="xs">
                            <Button
                              size="xs"
                              variant="light"
                              leftSection={<IconFileUpload size={14} />}
                              onClick={() => {
                                setCsvImportPageId(page.id);
                                setCsvImportFile(null);
                              }}
                            >
                              Add from CSV
                            </Button>
                            <Button
                              size="xs"
                              variant="light"
                              leftSection={<IconPlus size={14} />}
                              onClick={() =>
                                setCreatingItem(creatingItem === page.id ? null : page.id)
                              }
                            >
                              {creatingItem === page.id ? "Cancel" : "Add Item"}
                            </Button>
                          </Group>
                        </Group>

                        {/* Create New Menu Item Form */}
                        {creatingItem === page.id && (
                          <Card withBorder p="md" style={{ backgroundColor: "#f8f9fa" }}>
                            <Stack gap="md">
                              <Text fw={500} size="sm">Create New Menu Item</Text>
                              <Group grow>
                                <TextInput
                                  label="Name *"
                                  value={newItemData.name || ""}
                                  onChange={(e) =>
                                    setNewItemData({ ...newItemData, name: e.currentTarget.value })
                                  }
                                  placeholder="Item name"
                                />
                                <TextInput
                                  label="Category"
                                  value={newItemData.category || ""}
                                  onChange={(e) =>
                                    setNewItemData({
                                      ...newItemData,
                                      category: e.currentTarget.value,
                                    })
                                  }
                                  placeholder="Category"
                                />
                                <NumberInput
                                  label="Price *"
                                  value={newItemData.price ? Number(newItemData.price) : undefined}
                                  onChange={(value) =>
                                    setNewItemData({ ...newItemData, price: value ?? null })
                                  }
                                  decimalScale={2}
                                  min={0}
                                  prefix="¥"
                                  placeholder="0.00"
                                />
                              </Group>
                              <Textarea
                                label="Description"
                                minRows={2}
                                value={newItemData.description || ""}
                                onChange={(e) =>
                                  setNewItemData({
                                    ...newItemData,
                                    description: e.currentTarget.value,
                                  })
                                }
                                placeholder="Item description"
                              />
                              <Group justify="flex-end">
                                <Button
                                  size="sm"
                                  loading={creatingItem === page.id}
                                  onClick={() => handleCreateItem(page.id)}
                                  disabled={!newItemData.name || !newItemData.price || creatingItem === page.id}
                                  leftSection={<IconPlus size={14} />}
                                >
                                  Create Item
                                </Button>
                              </Group>
                            </Stack>
                          </Card>
                        )}

                        {page.menuItems && page.menuItems.length > 0 ? (
                          <Accordion>
                            {page.menuItems.map((item) => {
                              // Create ref if it doesn't exist
                              if (!itemEditorRefs.current[item.id]) {
                                itemEditorRefs.current[item.id] = React.createRef<MenuItemEditorRef>();
                              }
                              return (
                                <MenuItemEditor
                                  key={item.id}
                                  ref={itemEditorRefs.current[item.id]}
                                  item={item}
                                  onSave={handleSaveItem}
                                  onDelete={handleDeleteItemRequest}
                                  onRedoAnalysis={handleRedoAnalysisItem}
                                  onGenerateFaq={handleGenerateFaqItem}
                                  onGenerateImage={handleGenerateImageItem}
                                  onUploadImage={handleUploadItemImage}
                                  onDeleteImage={handleDeleteItemImage}
                                  isSaving={savingItemId === item.id}
                                  analysisLoading={itemAnalysisLoading[item.id] || false}
                                  faqLoading={itemFaqLoading[item.id] || false}
                                  imageLoading={itemImageLoading[item.id] || false}
                                  onChangesChange={handleItemChangesChange}
                                />
                              );
                            })}
                          </Accordion>
                        ) : (
                          <Text size="sm" c="dimmed">
                            No menu items in this page.
                          </Text>
                        )}
                      </Stack>
                    </Stack>
                  </Card>
                );
              })
            )}
          </Stack>
        </Tabs.Panel>
      </Tabs>

      {/* Delete Menu Item Confirmation Modal */}
      <Modal
        opened={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setItemToDelete(null);
        }}
        title="Delete Menu Item"
      >
        <Stack gap="md">
          <Text>
            Are you sure you want to delete "{itemToDelete?.name}"? This action cannot be undone.
          </Text>
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => {
                setDeleteModalOpen(false);
                setItemToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button color="red" onClick={handleDeleteItem}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Add menu items from CSV (same format as /admin/upload/csv) */}
      <Modal
        opened={csvImportPageId !== null}
        onClose={() => {
          setCsvImportPageId(null);
          setCsvImportFile(null);
        }}
        title="Add Items from CSV"
        size="md"
      >
        <Stack gap="md">
          <Alert title="CSV format (same as Upload CSV)" color="blue">
            <Text size="sm" mb="xs">
              Headers (case-insensitive): <Code>name</Code>, <Code>description</Code>, <Code>price</Code>, <Code>category</Code>, <Code>ingredients</Code>
            </Text>
            <Text size="xs" c="dimmed">
              name and price are required. ingredients: comma-separated list.
            </Text>
          </Alert>
          <Dropzone
            onDrop={(files) => setCsvImportFile(files[0] || null)}
            accept={{ "text/csv": [".csv"] }}
            maxFiles={1}
            disabled={csvImportLoading}
          >
            <Text ta="center" size="sm">
              Drag a CSV file here or click to select
            </Text>
          </Dropzone>
          {csvImportFile && (
            <Group justify="space-between">
              <Text size="sm">{csvImportFile.name}</Text>
              <Button size="xs" variant="subtle" color="red" onClick={() => setCsvImportFile(null)} disabled={csvImportLoading}>
                Remove
              </Button>
            </Group>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => { setCsvImportPageId(null); setCsvImportFile(null); }} disabled={csvImportLoading}>
              Cancel
            </Button>
            <Button onClick={handleImportCsvForPage} loading={csvImportLoading} disabled={!csvImportFile}>
              Import into this page
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* FAQ Test Preview Modal (does not persist to menu) */}
      <Modal
        opened={testFaqModalOpen}
        onClose={() => {
          setTestFaqModalOpen(false);
        }}
        title="Test FAQ (Preview)"
        size="lg"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Select a menu item, then generate a FAQ preview. This does <b>not</b> save anything back
            to the menu.
          </Text>

          <Select
            label="Menu item"
            placeholder={allMenuItems.length ? "Select a menu item" : "No menu items found"}
            data={testFaqSelectOptions}
            value={testFaqSelectedItemId}
            onChange={(value) => {
              setTestFaqSelectedItemId(value);
              setTestFaqResult(null);
            }}
            searchable
            nothingFoundMessage="No matching menu items"
            disabled={allMenuItems.length === 0}
          />

          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => setTestFaqModalOpen(false)}
              disabled={generatingTestFaq}
            >
              Close
            </Button>
            <Button
              leftSection={<IconWand size={16} />}
              onClick={handleGenerateTestFaq}
              loading={generatingTestFaq}
              disabled={!testFaqSelectedItemId}
            >
              Run Test
            </Button>
          </Group>

          {testFaqResult ? (
            <Card withBorder>
              <Stack gap="xs">
                <Text fw={600}>
                  Output for: {testFaqResult?.name ?? "Selected item"}
                </Text>
                <Text size="sm" c="dimmed">
                  Below is the generated FAQ payload returned by the API.
                </Text>
                <Textarea
                  readOnly
                  minRows={12}
                  value={JSON.stringify(testFaqResult?.faq ?? {}, null, 2)}
                  style={{ fontFamily: "monospace" }}
                />
              </Stack>
            </Card>
          ) : null}
        </Stack>
      </Modal>

      {/* Delete Menu Page Confirmation Modal */}
      <Modal
        opened={deletePageModalOpen}
        onClose={() => {
          setDeletePageModalOpen(false);
          setPageToDelete(null);
        }}
        title="Delete Menu Page"
      >
        <Stack gap="md">
          <Text>
            Are you sure you want to delete this menu page? This will also delete all menu items
            in this page ({pageToDelete?.menuItems?.length || 0} items). This action cannot be
            undone.
          </Text>
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => {
                setDeletePageModalOpen(false);
                setPageToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button color="red" onClick={handleDeletePage}>
              Delete Page
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Rename Category Modal */}
      <Modal
        opened={categoryRenameModalOpen}
        onClose={() => {
          setCategoryRenameModalOpen(false);
          setCategoryToRename(null);
          setNewCategoryName("");
        }}
        title="Rename Category"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            This will update all menu items with the category "{categoryToRename}" to use the new category name.
          </Text>
          <TextInput
            label="Old Category Name"
            value={categoryToRename || ""}
            disabled
            readOnly
          />
          <TextInput
            label="New Category Name"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.currentTarget.value)}
            placeholder="Enter new category name"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newCategoryName.trim() && newCategoryName.trim() !== categoryToRename) {
                handleRenameCategory();
              }
            }}
          />
          {(() => {
            const itemCount = categoryToRename 
              ? allMenuItems.filter((item) => {
                  return itemHasCategory(item, categoryToRename);
                }).length
              : 0;
            
            return itemCount > 0 ? (
              <Alert color="blue" title="Items to Update">
                <Text size="sm">
                  {itemCount} menu {itemCount === 1 ? 'item' : 'items'} will be updated.
                </Text>
              </Alert>
            ) : null;
          })()}
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => {
                setCategoryRenameModalOpen(false);
                setCategoryToRename(null);
                setNewCategoryName("");
              }}
              disabled={renamingCategory}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRenameCategory}
              loading={renamingCategory}
              disabled={!newCategoryName.trim() || newCategoryName.trim() === categoryToRename}
            >
              Rename Category
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Dedupe Review Modal */}
      <Modal
        opened={dedupeModalOpen}
        onClose={() => {
          setDedupeModalOpen(false);
          setDedupeGroups([]);
          setDedupeSelected(new Set());
        }}
        title={
          <Group gap="xs">
            <Text fw={600}>Review Duplicate Items</Text>
            <Badge variant="light" color="red">{dedupeSelected.size} selected for deletion</Badge>
          </Group>
        }
        size="xl"
        scrollAreaComponent={undefined}
      >
        <Stack gap="lg">
          <Text size="sm" c="dimmed">
            Items are grouped by name. The top item in each group (highlighted) is recommended to keep — it has the richest data. Check any items you want to delete, then click <strong>Delete Selected</strong>.
          </Text>

          {dedupeGroups.map((group) => (
            <Card key={group.name} withBorder p="sm">
              <Text fw={600} mb="xs" size="sm">{group.name}</Text>
              <Stack gap="xs">
                {group.items.map((item, idx) => {
                  const isChecked = dedupeSelected.has(item.id);
                  const isRecommendedKeep = idx === 0;
                  return (
                    <Box
                      key={item.id}
                      p="xs"
                      style={{
                        border: `1px solid ${isRecommendedKeep ? "var(--mantine-color-green-4)" : "var(--mantine-color-default-border)"}`,
                        borderRadius: "var(--mantine-radius-sm)",
                        backgroundColor: isRecommendedKeep ? "var(--mantine-color-green-0)" : undefined,
                        opacity: isChecked ? 0.5 : 1,
                      }}
                    >
                      <Group justify="space-between" wrap="nowrap">
                        <Group gap="xs" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                          <input
                            type="checkbox"
                            checked={isChecked}
                            disabled={isRecommendedKeep && dedupeGroups.find(g => g.name === group.name)!.items.filter(i => !dedupeSelected.has(i.id)).length <= 1}
                            onChange={(e) => {
                              setDedupeSelected(prev => {
                                const next = new Set(prev);
                                if (e.currentTarget.checked) {
                                  next.add(item.id);
                                } else {
                                  next.delete(item.id);
                                }
                                return next;
                              });
                            }}
                            style={{ cursor: "pointer", flexShrink: 0 }}
                          />
                          <Stack gap={2} style={{ minWidth: 0 }}>
                            <Group gap="xs" wrap="nowrap">
                              {isRecommendedKeep && <Badge size="xs" color="green">Keep</Badge>}
                              <Text size="xs" fw={500} lineClamp={1}>{item.name}</Text>
                            </Group>
                            <Group gap="xs" wrap="nowrap">
                              <Text size="xs" c="dimmed" style={{ fontFamily: "monospace" }}>{item.id.slice(0, 8)}…</Text>
                              {item.price != null && <Text size="xs" c="dimmed">¥{item.price}</Text>}
                              {item.category && <Badge size="xs" variant="outline">{item.category}</Badge>}
                            </Group>
                            {item.description && (
                              <Text size="xs" c="dimmed" lineClamp={2}>{item.description}</Text>
                            )}
                          </Stack>
                        </Group>
                        <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
                          {item.imageUrl && <Badge size="xs" color="blue" variant="light">img</Badge>}
                          <Badge size="xs" variant="light" color="gray">score {item.score}</Badge>
                        </Group>
                      </Group>
                    </Box>
                  );
                })}
              </Stack>
            </Card>
          ))}

          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => {
                setDedupeModalOpen(false);
                setDedupeGroups([]);
                setDedupeSelected(new Set());
              }}
              disabled={dedupeDeleting}
            >
              Cancel
            </Button>
            <Button
              color="red"
              loading={dedupeDeleting}
              disabled={dedupeSelected.size === 0}
              onClick={handleDedupeDelete}
            >
              Delete {dedupeSelected.size} item{dedupeSelected.size !== 1 ? "s" : ""}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Save All Button at bottom */}
      {itemsWithChanges.size > 0 && (
        <Card withBorder mt="xl" style={{ position: 'sticky', bottom: 20, zIndex: 100 }}>
          <Group justify="space-between" align="center">
            <Text fw={500}>
              {itemsWithChanges.size} item{itemsWithChanges.size !== 1 ? 's' : ''} with unsaved changes
            </Text>
            <Button
              size="lg"
              onClick={handleSaveAllItems}
              loading={Array.from(itemsWithChanges).some(id => savingItemId === id)}
            >
              Save All Items
            </Button>
          </Group>
        </Card>
      )}
    </Stack>
  );
}
